<template>
  <div class="cd-page">

    <!-- 顶部导航 -->
    <!-- 顶部导航：进入任意单据页后隐藏，由 cd-tabbar 承担标题与返回 -->
    <van-nav-bar
      v-if="!billTab"
      class="cd-nav"
      fixed
      placeholder
      :title="contract.name ? contract.name : '合同详情'"
      left-text="返回"
      left-arrow
      @click-left="onNavLeft"
    >
      <!-- 【P108f】按用户要求：**去掉右上角的「删除」**（销售人员的删除入口），
           删除统一放到页面底部的详情操作条（仅管理人员可见，样式与商机详情一致） -->
    </van-nav-bar>

    <!-- 【P24-1】总览区下拉刷新：覆盖 Hero + 合同信息 + 业务单据宫格（原先只有单据页可下拉） -->
    <van-pull-refresh v-if="!billTab" v-model="overviewRefreshing" @refresh="onOverviewRefresh">
    <!-- 合同头部 Hero（蓝底：销售金额单独放大 + 四个金额指标） -->
    <div class="cd-hero" v-if="loaded && !billTab">
      <div class="cd-hero__main">
        <div class="cd-hero__main-label">销售金额（实际销售金额）</div>
        <div class="cd-hero__main-value">¥{{ fmtMoney(contract.sales_amount) }}</div>
      </div>
      <div class="cd-hero__metrics">
        <div class="cd-metric">
          <div class="cd-metric__label">订单总金额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.total_amount) }}</div>
        </div>
        <div class="cd-metric">
          <div class="cd-metric__label">支付金额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.payment_amount) }}</div>
        </div>
        <div class="cd-metric">
          <div class="cd-metric__label">待支付金额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.pending_payment_amount) }}</div>
        </div>
        <div class="cd-metric">
          <div class="cd-metric__label">当前余额</div>
          <div class="cd-metric__value">¥{{ fmtMoney(contract.balance_amount) }}</div>
        </div>
      </div>
    </div>

    <!-- 【P97 三.00】管理人员审批区：**状态常显**（待审批 / 已通过 / 已驳回），
         仅「待审批 + 当前登录人有权处理」时才出现【通过】【驳回】按钮。
         此前只在 pending 时渲染 → 已通过/已驳回的合同详情里完全看不到审批痕迹，
         用户感受就是「前端页面没有审核面」。 -->
    <div class="cd-approval" :class="{ 'cd-approval--pass': approvalState==='pass', 'cd-approval--reject': approvalState==='reject' }"
         v-if="loaded && !billTab && approvalInfo && approvalInfo.id">
      <div class="cd-approval__top">
        <span class="cd-approval__badge">{{ approvalInfo.status_name || '待审批' }}</span>
        <span class="cd-approval__who" v-if="approvalInfo.approval_user_name">审批人：{{ approvalInfo.approval_user_name }}</span>
      </div>
      <div class="cd-approval__desc" v-if="approvalInfo.pending">
        该销售合同已提交管理人员审批，<b>通过后生效</b>；驳回将退回可编辑。
      </div>
      <div class="cd-approval__desc cd-approval__desc--reject" v-else-if="approvalState==='reject'">
        审批未通过{{ approvalInfo.mssage ? '：' + approvalInfo.mssage : '' }}
      </div>
      <div class="cd-approval__desc" v-else>
        该销售合同审批已通过，合同已生效。
      </div>
      <!-- 【P105】审核操作已迁到「销售合同 → 审核」列表页（支持单条 + 全选批量）；
           详情页只保留只读展示 + 跳转入口，避免「在合同详情页直接审核」的旧交互。 -->
      <div class="cd-approval__bar" v-if="approvalInfo.pending">
        <van-button block round type="primary" @click="goApprovalTab">去「审核」列表处理</van-button>
      </div>
    </div>


    <van-loading v-if="!loaded && !billTab" class="cd-loading" type="spinner" color="#2b5fd9" />

    <!-- 合同概要：名称 / 编号 / 状态 / 类型 -->
    <div class="cd-info" v-if="loaded && !billTab">
      <div class="cd-info__grid">
        <div class="cd-info__item cd-info__item--full">
          <span class="cd-info__label">合同名称</span>
          <span class="cd-info__value">{{ contract.name || '—' }}</span>
        </div>
        <div class="cd-info__item">
          <span class="cd-info__label">合同编号</span>
          <span class="cd-info__value">{{ contract.serial_number || '—' }}</span>
        </div>
        <div class="cd-info__item">
          <span class="cd-info__label">状态</span>
          <span class="cd-info__value">
            <van-tag :type="statusTagType" plain>{{ contract.status_name || '—' }}</van-tag>
          </span>
        </div>
        <div class="cd-info__item">
          <span class="cd-info__label">类型</span>
          <span class="cd-info__value">{{ contract.type_name || '—' }}</span>
        </div>
        <div class="cd-info__item cd-info__item--full">
          <span class="cd-info__label">所属客户</span>
          <span class="cd-info__value">{{ contract.customer_name || '—' }}</span>
        </div>
      </div>
      <!-- 【P108f】编辑入口统一到底部详情操作条（与商机详情同款：删除 + 编辑） -->
    </div>

    <!-- 【P108f】合同详情操作条：与「商机详情」完全同款（.cust-detail__bar + .btn.btn--*）
         · 删除：仅管理人员可见（后端 ordersDelete 同口径强校验）
         · 编辑：与商机一致放在这里（原 Hero 里的零散编辑按钮已移除） -->
    <div class="cust-detail__bar cust-detail__bar--inline" v-if="loaded && !billTab">
      <button v-if="canManageDelete" class="btn btn--secondary opp-del"
              :disabled="contractDeleting" @click="askDeleteContract">
        <span v-if="contractDeleting">删除中…</span><span v-else>删除</span>
      </button>
      <button class="btn btn--primary" @click="onEditContractClick">编辑合同</button>
    </div>

    <!-- 进度情况：stage 1待执行 / 2已预定 / 3已支付 / 4已全款 -->
    <div class="cd-panel" v-if="loaded && !billTab">
      <div class="cd-panel__hd">
        <span class="cd-sec__title">进度情况</span>
        <span class="cd-progress__pct">{{ stageText }} · {{ stagePct }}%</span>
      </div>
      <div class="cd-progress__bar">
        <div class="cd-progress__fill" :style="{ width: stagePct + '%' }"></div>
      </div>
      <div class="cd-steps">
        <div
          class="cd-step"
          v-for="s in stages"
          :key="s.value"
          :class="{ 'cd-step--on': stageIdx >= s.value, 'cd-step--cur': stageIdx === s.value }"
        >
          <div class="cd-step__dot">
            <van-icon v-if="stageIdx > s.value" name="success" />
            <span v-else>{{ s.value }}</span>
          </div>
          <div class="cd-step__label">{{ s.label }}</div>
        </div>
      </div>
    </div>

    <!-- 合同信息：对齐后台详情配置（业务信息 / 会议信息 / 系统信息），三组默认收起 -->
    <template v-if="loaded && !billTab">
      <div class="cd-panel" v-for="g in fieldGroups" :key="g.key">
        <div class="cd-fold" @click="toggleGroup(g.key)">
          <span class="cd-fold__title">{{ g.title }}</span>
          <span class="cd-fold__hint">{{ groupOpen[g.key] ? '收起' : '展开' }}</span>
          <van-icon :name="groupOpen[g.key] ? 'arrow-up' : 'arrow-down'" class="cd-fold__arrow" />
        </div>

        <div v-show="groupOpen[g.key]" class="cd-fold__body">
          <!-- 金额类字段：两列宫格 -->
          <div class="cd-mgrid" v-if="g.money.length">
            <!-- 【P36-fix】money 允许「同名不同口径」重复展示（如 total_deposits 出现两次），
                 :key 不能用 f.prop（会触发 Vue Duplicate keys 警告），改用 组key+prop+序号 保证唯一 -->
            <div class="cd-mgrid__cell" v-for="(f, mi) in g.money" :key="'m-' + g.key + '-' + f.prop + '-' + mi">
              <div class="cd-mgrid__label">{{ f.label }}</div>
              <div class="cd-mgrid__value">{{ moneyText(f.prop) }}</div>
            </div>
          </div>
          <!-- 文本类字段：整行 -->
          <div class="cd-info-row" v-for="(f, ri) in g.rows" :key="'r-' + g.key + '-' + f.prop + '-' + ri">
            <span class="cd-info-row__label">{{ f.label }}</span>
            <span class="cd-info-row__value">{{ f.strip ? plainText(contract[f.prop]) : textOf(f.prop) }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- 业务单据：小图标宫格，点击进入独立页面 -->
    <div class="cd-panel" v-if="loaded && !billTab">
      <div class="cd-panel__hd">
        <span class="cd-sec__title">业务单据</span>
        <span class="cd-panel__hint">点击进入</span>
      </div>
      <div class="cd-grid">
        <div class="cd-grid__item" v-for="tab in tabConfigs" :key="tab.key" @click="openBill(tab)">
          <div class="cd-grid__icon">
            <van-icon :name="tab.icon" />
            <!-- 04 数量改为标题括号展示；红色气泡：会议室订单走后端权威计数（P107），其余单据用「驳回类」行数 -->
            <span class="cd-grid__badge" v-if="gridBadge(tab)">{{ gridBadge(tab) }}</span>
          </div>
          <div class="cd-grid__label">{{ tab.short || tab.title }}{{ (lists[tab.key]||[]).length ? ' (' + (lists[tab.key]||[]).length + ')' : '' }}</div>
        </div>
      </div>
    </div>
    </van-pull-refresh>

    <!-- 单据独立页面：点击宫格图标后覆盖展示，返回不跳路由 -->
    <div class="cd-billpage" v-if="billTab">
      <div class="cd-tabbar">
        <div class="cd-tabbar__top">
          <div class="cd-tabbar__left" @click="billFormMode ? closeForm() : closeBill()">
            <van-icon name="arrow-left" class="cd-tabbar__back" />
            <!-- 表单已改为底部弹窗（自带标题），这里始终显示单据名，避免标题重复 -->
            <span class="cd-tabbar__title">{{ billTab ? billTab.title : '' }}</span>
          </div>
          <template v-if="!billFormMode">
            <div class="cd-tabbar__search">
              <van-icon name="search" class="cd-tabbar__search-icon" />
              <!-- 【P108】修复「搜索无效」：原 input 未绑任何事件 ——
                   声明了 filters 的单据页（如会议室订单）本地过滤被禁用，又从不发服务端请求，等于摆设。
                   现在回车/失焦/清空都会触发服务端搜索（onBillSearchSubmit）。 -->
              <input v-model="billSearch" type="text" placeholder="搜索"
                     @change="onBillSearchSubmit" @keyup.enter="onBillSearchSubmit" />
              <van-icon v-if="billSearch" name="clear" class="cd-tabbar__search-clear"
                        @click.stop="billSearch=''; onBillSearchSubmit()" />
            </div>
            <div class="cd-tabbar__actions">
              <!-- 【P105】审批列表：批量模式开关（单一审核 + 全选批量，与客户分配同一交互） -->
              <div class="cd-tabbar__btn" v-if="isApprovalTab" :class="{ 'cd-tabbar__btn--active': approvalBatch }" @click="toggleApprovalBatch">
                <van-icon name="records" />
                <span>{{ approvalBatch ? '取消批量' : '批量' }}</span>
              </div>
              <div class="cd-tabbar__btn" :class="{ 'cd-tabbar__btn--active': billFilterActive }" @click="openSortSheet">
                <van-icon name="filter-o" />
                <span>筛选</span>
              </div>
              <!-- 只读单据（审批 / 余额明细）：不显示「新增」 -->
              <div class="cd-tabbar__btn cd-tabbar__btn--primary" v-if="!billTab.readonly" @click="openForm('add')">
                <van-icon name="plus" />
                <span>新增</span>
              </div>
            </div>
          </template>
        </div>
        <!-- 【P105】审批列表：批量态全选条 -->
        <div class="apx-batch-bar" v-if="isApprovalTab && approvalBatch">
          <van-checkbox :value="approvalAllSelected" @input="toggleApprovalSelectAll">全选待审核</van-checkbox>
          <span class="apx-batch-bar__count">已选 {{ approvalSelected.length }} 条</span>
        </div>
        <!-- 【P106】审核操作结果横幅：不依赖 toast，任何提前返回/失败都必可见（成功 4s 自动消失，失败常驻） -->
        <div class="apx-result" :class="'apx-result--' + approvalResult.type" v-if="approvalResult.text">
          <span class="apx-result__dot"></span>
          <span class="apx-result__txt">{{ approvalResult.text }}</span>
          <span class="apx-result__x" @click="clearApprovalResult">✕</span>
        </div>
        <!-- 【P106】审核 tab 无「待我处理」行时的明确说明（避免用户以为按钮坏了） -->
        <div class="apx-result apx-result--info"
             v-if="isApprovalTab && !approvalBatch && approvalPendingRows > 0 && approvalMineCount === 0">
          <span class="apx-result__dot"></span>
          <span class="apx-result__txt">本合同有 {{ approvalPendingRows }} 条待审核，但都不是「当前登录人」负责的（审批人 / 管理者），因此不可操作。</span>
        </div>
        <div class="cd-tabbar__summary" v-if="!billFormMode">
          <span>共 {{ filteredBillTotal.count }} 条</span>
          <span v-if="filteredBillTotal.hasSum">合计 {{ billMoney(filteredBillTotal.sum) }}</span>
        </div>
        <!-- 【P206】已生效筛选条件（与「快捷入口」同款条件条）：让用户一眼看清当前筛选了什么 -->
        <div class="filter-status-bar" v-if="!billFormMode && billFilterLabels.length">
          <span class="filter-status-bar__txt">筛选：{{ billFilterLabels.join(' · ') }}</span>
          <span class="filter-status-bar__clear" @click="onRoomFilterClear">清空</span>
        </div>
      </div>

      <!-- ============ 列表页：与主列表（客户/商机/合同）完全同一套卡片样式 ============ -->
      <!-- 表单已改为底部弹窗，列表常驻作为背景（不再随 billFormMode 隐藏） -->
      <template>
        <!-- 下拉刷新 -->
        <van-pull-refresh v-model="billRefreshing" @refresh="onBillRefresh">
          <!-- 空态 -->
          <div class="bl-empty" v-if="!filteredBillRows.length">
            <div class="bl-empty__icon">
              <van-icon :name="billTab.icon" />
            </div>
            <div class="bl-empty__text">暂无{{ billTab.title }}</div>
            <div class="bl-empty__hint" v-if="!billTab.readonly">点击右上角「新增」创建</div>
            <div class="bl-empty__hint" v-else>{{ billTab.emptyHint || '仅可查看' }}</div>
          </div>

          <!-- 卡片列表：crm-item + customer-card + status-tag + btn-action -->
          <div class="list-box" v-else>
            <div class="crm-item" v-for="(item,i) in filteredBillRows" :key="item.row.sn || i"
                 :data-sn="item.row.sn"
                 :class="{ 'apx-item--batch': isApprovalTab && approvalBatch,
                           'crm-item--rejected': !isApprovalTab && rowRejected(item.row) }">
              <!-- 【P105】审批列表批量态：行选择（已处理/非合同级的行不可选） -->
              <van-checkbox v-if="isApprovalTab && approvalBatch" class="apx-item__check"
                            :value="isApprovalSelected(item.row.sn)"
                            :disabled="!approvalRowCanDo(item.row)"
                            @input="v => onApprovalSelect(item.row.sn, v)" />
              <div class="item-head customer-card" @click="isApprovalTab && approvalBatch ? toggleApprovalRow(item.row) : openRowDetail(item.row)">
                <!-- 【P105g2】审核 tab 专用卡片：审批内容「全量换行展示」+ 行内【✗ 驳回】【✓ 通过】，
                     一眼可读、直接操作，无需点击进入详情 -->
                <template v-if="isApprovalTab">
                  <div class="apx-card">
                    <div class="apx-card__head">
                      <span class="apx-card__type">{{ item.row.process_id_name || '合同/订单审批' }}</span>
                      <span class="apx-card__scope"
                            :class="item.row.source_sn ? 'apx-card__scope--room' : 'apx-card__scope--contract'">
                        {{ item.row.approval_scope_name || (item.row.source_sn ? '会议室订单' : '销售合同') }}
                      </span>
                      <span class="status-tag" :class="item.statusClass" v-if="item.status">{{ item.status }}</span>
                    </div>
                    <div class="apx-card__desc">{{ approvalRowText(item.row) }}</div>
                    <div class="apx-card__meta">
                      <span v-if="item.row.create_user_name">提交人：{{ item.row.create_user_name }}</span>
                      <span v-if="item.row.approval_user_name">审批人：{{ item.row.approval_user_name }}</span>
                      <span v-if="item.row.create_time">提交时间：{{ item.row.create_time }}</span>
                    </div>
                    <div class="apx-card__remark" v-if="item.row.mssage">
                      <em>审批意见</em>{{ item.row.mssage }}
                    </div>
                  </div>
                  <!-- 【P107】来源是会议室订单的审核 → 一键跳到对应会议室订单（定位 + 高亮） -->
                  <div class="apx-card__link" v-if="item.row.source_sn" @click.stop="goRoomOrderTab(item.row.source_sn)">
                    <van-icon name="calendar-o" class="apx-card__link-ico" />
                    <span class="apx-card__link-txt">查看会议室订单</span>
                    <van-icon name="arrow" class="apx-card__link-arrow" />
                  </div>
                  <!-- 【P105g】单条审核操作（合同级 source_sn 空 / 会议室订单·价格级 source_sn 非空 均可） -->
                  <div class="customer-card__actions apx-actions" v-if="!approvalBatch && approvalRowCanDo(item.row)">
                    <div class="btn-action btn-action--danger" @click.stop="approvalDoOne(item.row, 'reject')">✗ 驳回</div>
                    <div class="btn-action btn-action--primary" @click.stop="approvalDoOne(item.row, 'pass')">✓ 通过</div>
                  </div>
                  <div class="customer-card__actions apx-hint" v-else-if="!approvalBatch && approvalRowPending(item.row)">
                    <span class="apx-hint__txt">当前登录人不是该审批的审批人 / 管理者，无法处理</span>
                  </div>
                </template>
                <!-- 其它单据：沿用通用卡片 -->
                <template v-else>
                  <div class="customer-card__main">
                    <div class="customer-card__content">
                      <div class="customer-card__header">
                        <div class="customer-card__name-wrap">
                          <span class="customer-card__name">{{ item.title }}</span>
                          <span class="customer-card__role" v-if="item.amountText">{{ item.amountText }}</span>
                        </div>
                        <span class="status-tag" :class="item.statusClass" v-if="item.status">{{ item.status }}</span>
                      </div>
                      <!-- 【P107】审核状态标签：挂了 approval_status_name 的单据才显示（会议室订单）
                           —— 等待审核 / 通过 / 未通过，未通过时整行标红（.crm-item--rejected） -->
                      <div class="customer-card__row bill-flags" v-if="item.row.approval_status_name">
                        <span class="status-tag" :class="billStatusClass(item.row.approval_status_name)">
                          审核状态：{{ item.row.approval_status_name }}
                        </span>
                      </div>
                      <div class="customer-card__row">{{ item.row1 }}</div>
                      <div class="customer-card__row customer-card__row--muted">{{ item.row2 }}</div>
                    </div>
                  </div>
                  <!-- 只读单据（余额明细）：不显示编辑与删除，仅可点卡片查看详情 -->
                  <div class="customer-card__actions" v-if="!billTab.readonly">
                    <div class="btn-action btn-action--primary" v-if="billTab.editFn && rowEditable(item.row)" @click.stop="openForm('edit', item.row)">✎ 编辑</div>
                    <div class="btn-action btn-action--primary" v-if="canSubmitRow(item.row)" @click.stop="submitRow(item.row)">⇪ 提交</div>
                    <div class="btn-action btn-action--danger" v-if="billTab.deleteFn && rowDeletable(item.row)" @click.stop="deleteRoomOrder(item.row)">🗑 删除</div>
                  </div>
                </template>
              </div>
            </div>
          </div>
          </van-pull-refresh>

          <!-- 【P105】审批列表：底部批量操作栏 -->
          <div class="apx-batch-actions" v-if="isApprovalTab && approvalBatch && approvalSelected.length > 0">
            <span class="apx-batch-actions__count">已选 {{ approvalSelected.length }} 条</span>
            <div class="apx-batch-actions__btns">
              <van-button size="small" type="primary" :loading="approvalSubmitting" @click="approvalDoBatch('pass')">全部通过</van-button>
              <van-button size="small" type="danger" :loading="approvalSubmitting" @click="approvalDoBatch('reject')">全部驳回</van-button>
            </div>
          </div>

              <!-- 【P214】关联会议室订单：可搜索多选弹层（选项可达上百条） -->
              <van-popup v-model="multiPickShow" round position="bottom" :style="{ height: '72%' }">
                <div class="form-page">
                  <div class="form-title">{{ multiPickTitle }}</div>
                  <div class="form-body">
                    <van-search v-model="multiPickKw" placeholder="搜索名称 / 日期 / 金额" />
                    <div class="ro-hint">共 {{ multiPickFiltered.length }} 项（已选 {{ multiPickSelected.length }}）</div>
                    <div class="ro-chips">
                      <span class="ro-chip ro-chip--all"
                        :class="{'ro-chip--on': multiPickSelected.length === multiPickFiltered.length && multiPickFiltered.length > 0}"
                        @click="multiPickToggleAll">全选</span>
                      <span v-for="o in multiPickFiltered" :key="o.value" class="ro-chip"
                        :class="{'ro-chip--on': multiPickSelected.indexOf(o.value) > -1}"
                        @click="multiPickToggle(o.value)">{{ o.label }}</span>
                    </div>
                    <div class="ro-hint" v-if="!multiPickFiltered.length">{{ multiPickOptions.length ? '没有匹配项' : '本销售合同下暂无可关联的会议室订单' }}</div>
                  </div>
                  <div class="form-footer">
                    <div class="ro-btn ro-btn--cancel" @click="multiPickShow=false">取消</div>
                    <div class="ro-btn ro-btn--save" @click="multiPickConfirm">确定</div>
                  </div>
                </div>
              </van-popup>

          <!-- 单据详情（只读）：点卡片弹出，容器与表单同款；编辑走底部按钮 -->
          <van-popup v-model="detailShow" round position="bottom" :style="{ height: '88%' }">
            <div class="form-page">
              <div class="form-title">{{ billTab ? billTab.title : '' }}详情</div>
              <div class="form-body">
                <div class="ro-group ro-group--card" v-for="(g,gi) in detailGroups" :key="gi">
                  <div class="ro-group__hd">{{ g.name }}</div>
                  <div class="ro-field" v-for="(f,fi) in g.fields" :key="fi">
                    <span class="ro-label">{{ f.label }}</span>
                    <!-- 【P214】图片凭证：缩略图 + 点击查看大图（复用 Vant ImagePreview） -->
                    <img v-if="f.isImage && f.value"
                         class="ro-img-thumb" :src="f.value" @click="previewImage(f.value)" />
                    <span v-else class="ro-val ro-val--right">{{ f.money ? billMoney(f.value) : f.value }}</span>
                  </div>
                </div>
                <!-- 【P97】三.3.05 一致性：合同详情内的单据详情也要展示「回款计划」
                     （原来 detailGroups 显式跳过 plans → 同一张会议室订单在
                       房态/会议室订单 Tab 详情里能看到回款计划，在这里看不到） -->
                <div class="ro-group ro-group--card" v-if="detailPlans.length">
                  <div class="ro-group__hd">回款计划</div>
                  <div class="ro-field" v-for="(p,pi) in detailPlans" :key="'dp'+pi">
                    <span class="ro-label">{{ p.date || '—' }}</span>
                    <span class="ro-val ro-val--right">{{ p.amount !== '' ? billMoney(p.amount) : '' }}</span>
                  </div>
                </div>
                <!-- 【P16】财务合同详情：底部关联区块（会议室/回款单/发票/退款记录） -->
                <div class="ro-group ro-group--card" v-for="(g,gi) in detailRelated" :key="'rel'+gi">
                  <div class="ro-group__hd">{{ g.label }}（{{ g.items.length }}）</div>
                  <div class="ro-field" v-for="(it,ii) in g.items" :key="ii">
                    <span class="ro-label">{{ it.text }}</span>
                    <span class="ro-val ro-val--right">{{ it.amount != null && it.amount !== '' ? billMoney(it.amount) : '' }}{{ it.status ? ' · ' + it.status : '' }}</span>
                  </div>
                  <div class="ro-empty" v-if="!g.items.length">暂无{{ g.label }}</div>
                </div>
                <div class="ro-empty" v-if="!detailGroups.length && !detailRelated.length && !detailPlans.length">该单据暂无可展示字段</div>
              </div>
              <div class="form-footer">
                <van-button block plain @click="detailShow=false">关闭</van-button>
                <van-button block type="info" v-if="billTab && billTab.editFn && !billTab.readonly && rowEditable(detailRow)" @click="onDetailEdit">编辑</van-button>
              </div>
            </div>
          </van-popup>
      </template>

      <!-- ============ 底部弹窗表单：新增 / 编辑（对齐客户表单 form-page 容器） ============ -->
      <van-popup
        :value="!!billFormMode"
        @click-overlay="closeForm"
        round
        position="bottom"
        :style="{ height: '88%' }"
      >
        <div class="cd-formpage">
          <div class="form-title">{{ billFormMode==='edit' ? '编辑'+(billTab?billTab.title:'') : '添加'+(billTab?billTab.title:'') }}</div>
          <div class="cd-formpage__body">
          <div class="ro-group" v-for="g in addGroups" :key="g.name">
            <div class="ro-group__hd">{{ g.name }}</div>

            <template v-for="f in g.fields">
              <!-- 【P9】showIf 联动显隐：字段声明 showIf(form) 时按当前表单值判断 -->
              <template v-if="fieldVisible(f)">
              <!-- 【P100】提示条（如「余额支付免财务审核」）：纯展示，不进表单提交 -->
              <div class="ro-notice" :class="{'ro-notice--warn': f.tone==='warn'}" v-if="f.type==='notice'" :key="f.prop">
                <van-icon name="info-o" class="ro-notice__ic" />
                <span class="ro-notice__tx">{{ f.text || f.label }}</span>
              </div>

              <!-- 日期：只读单元格 + 日期选择器 -->
              <div class="ro-field" v-else-if="f.type==='date'" :key="f.prop" @click="openDatePick(f.prop)">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val" :class="{'ro-val--ph': !addForm[f.prop]}">{{ addForm[f.prop] || '请选择' }}</span>
                <span class="ro-arrow">›</span>
              </div>

              <!-- 下拉选择：只读单元格 + ActionSheet；带 readonly 的字段（如「合同」）不可点选 -->
              <div class="ro-field" v-else-if="f.type==='select' && f.readonly" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val ro-val--right">{{ selectText(f) || '—' }}</span>
                <span class="ro-lock"><van-icon name="lock" /></span>
              </div>
              <div class="ro-field" v-else-if="f.type==='select'" :key="f.prop" @click="openSheet(f)">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val" :class="{'ro-val--ph': !selectText(f)}">{{ selectText(f) || (f.placeholder || '请选择') }}</span>
                <span class="ro-arrow">›</span>
              </div>

              <!-- 【P9/P214】多选块（关联会议室订单）：chips 风格多选 + 全选；提交逗号分隔 sn
                   【P214】选项多时（会议室订单可达上百条）平铺 chips 会撑爆页面，
                   改为「已选摘要行 + 点击弹层可搜索多选」；小选项集仍走原 chips 形态。 -->
              <div class="ro-block" v-else-if="f.type==='multiselect'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <template v-if="isMultiBig(f)">
                  <div class="ro-hint" v-if="!(f.options||[]).length">{{ f.placeholder || '加载选项中…' }}</div>
                  <div class="ro-multi-picker" v-else @click="openMultiPicker(f)">
                    <div class="ro-multi-picker__txt">
                      <template v-if="(addForm[f.prop]||[]).length">
                        已选 {{ (addForm[f.prop]||[]).length }} / {{ (f.options||[]).length }} 项
                        <span class="ro-multi-picker__sub">{{ multiPickLabel(f) }}</span>
                      </template>
                      <template v-else>点击选择（共 {{ (f.options||[]).length }} 项）</template>
                    </div>
                    <span class="ro-multi-picker__arrow">›</span>
                  </div>
                </template>
                <template v-else>
                  <div class="ro-hint" v-if="!(f.options||[]).length">{{ f.placeholder || '加载选项中…' }}</div>
                  <div class="ro-chips">
                    <span
                      class="ro-chip ro-chip--all"
                      :class="{'ro-chip--on': isMultiAllOn(f)}"
                      @click="toggleMultiAll(f)"
                    >全选</span>
                    <span
                      v-for="o in (f.options||[])"
                      :key="o.value"
                      class="ro-chip"
                      :class="{'ro-chip--on': isChipOn(addForm[f.prop], o.value)}"
                      @click="toggleMulti(f, o.value)"
                    >{{ o.label }}</span>
                  </div>
                  <div class="ro-hint" v-if="(addForm[f.prop]||[]).length">已选 {{ (addForm[f.prop]||[]).length }} 项</div>
                </template>
              </div>

              <!-- 折扣率：只读，自动计算（销售价格 / 市场价格 × 10） -->
              <div class="ro-field" v-else-if="f.type==='discount'" :key="f.prop">
                <span class="ro-label">{{ f.label }}</span>
                <span class="ro-val" :class="{'ro-val--ph': !discountText}">
                  {{ discountText ? (discountText + ' 折') : (f.placeholder || '自动计算') }}
                </span>
              </div>

              <!-- 回款计划：日期 + 金额 多行 -->
              <div class="ro-block" v-else-if="f.type==='plans'" :key="f.prop">
                <span class="ro-label" v-if="f.label !== g.name">
                  <i v-if="isPlanRequired(f)" class="ro-req">*</i>{{ f.label }}
                </span>
                <div class="ro-hint" v-if="f.hint">{{ f.hint }}</div>
                <div class="ro-plans">
                  <div class="ro-plan" v-for="(p,i) in (addForm[f.prop]||[])" :key="i">
                    <div class="ro-plan__cell" @click="openDatePick(f.prop, i)">
                      <span :class="{'ro-val--ph': !p.date}">{{ p.date || '回款日期' }}</span>
                    </div>
                    <input class="ro-plan__cell ro-plan__cell--amt" type="number" v-model="p.amount" placeholder="金额（元）" />
                    <span class="ro-plan__del" @click="delPayPlan(i)">×</span>
                  </div>
                </div>
                <div class="ro-addplan" @click="addPayPlan">+ 添加回款计划</div>
              </div>

              <!-- 多选：chips -->
              <div class="ro-block" v-else-if="f.type==='chips'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <div class="ro-chips">
                  <span
                    class="ro-chip"
                    :class="{'ro-chip--on': isChipOn(addForm[f.prop], o.value)}"
                    v-for="o in f.options"
                    :key="o.value"
                    @click="toggleChip(f, o.value)"
                  >{{ o.label }}</span>
                </div>
              </div>

              <!-- 时间区间：开始 ~ 结束 -->
              <!-- 【P33 修复】由裸文本输入框改为「滑动时间选择器」：
                   原输入框允许任意字符，用户极易输成 "0830"（少冒号），
                   id=68804 即因此落库 start_time=["0830","20:30"]。
                   选择器默认 08:30 / 20:30，从根源消除手输格式错误。 -->
              <div class="ro-block" v-else-if="f.type==='range'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <div class="ro-range">
                  <div class="ro-input ro-input--pick" @click="openTimePick(f.prop,'begin')">
                    <span :class="{'ro-pick--ph': !(addForm[f.prop] && addForm[f.prop].begin)}">
                      {{ (addForm[f.prop] && addForm[f.prop].begin) || '08:30' }}
                    </span>
                  </div>
                  <span class="ro-tilde">~</span>
                  <div class="ro-input ro-input--pick" @click="openTimePick(f.prop,'end')">
                    <span :class="{'ro-pick--ph': !(addForm[f.prop] && addForm[f.prop].end)}">
                      {{ (addForm[f.prop] && addForm[f.prop].end) || '20:30' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- 数字：只读（如「市场价格」价目表定值）不可输入，带锁图标 -->
              <div class="ro-field" v-else-if="f.type==='number' && f.readonly" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <span class="ro-val ro-val--right" :class="{'ro-val--ph': !addForm[f.prop]}">{{ addForm[f.prop] || (f.placeholder || '自动带出') }}</span>
                <span class="ro-lock"><van-icon name="lock" /></span>
              </div>

              <!-- 数字 -->
              <div class="ro-field" v-else-if="f.type==='number'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <input class="ro-input" type="number" v-model="addForm[f.prop]" :placeholder="f.placeholder || ('请输入'+f.label)" />
              </div>

              <!-- 文本域 -->
              <div class="ro-block" v-else-if="f.type==='textarea'" :key="f.prop">
                <span class="ro-label" v-if="f.label !== g.name"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <!-- 备注（remark）走自研富文本（04）；其余文本域保持轻量 textarea -->
                <RichEditor
                  v-if="f.prop === 'remark'"
                  :value="addForm[f.prop]"
                  :placeholder="f.placeholder || ('请输入'+f.label+'（选填）')"
                  @input="v => $set(addForm, f.prop, v)"
                />
                <div class="ro-textarea" v-else :class="{'ro-textarea--alone': f.label === g.name}">
                  <textarea v-model="addForm[f.prop]" :placeholder="f.placeholder || ('请输入'+f.label+'（选填）')"></textarea>
                </div>
              </div>

              <!-- 图片上传（本地单张，van-uploader + 自定义 after-read 直传业务接口） -->
              <div class="ro-block" v-else-if="f.type==='upload'" :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <van-uploader
                  v-model="addForm[f.prop + '_list']"
                  :after-read="(file) => afterReadUpload(f, file)"
                  :delete="() => onUploadDelete(f)"
                  :max-count="f.maxCount || 1"
                  :accept="f.accept || 'image/*'"
                />
              </div>

              <!-- 普通文本（含 type='text'；【P97】优先用字段自带 placeholder，便于「人数」等字段提示「可填文字」） -->
              <div class="ro-field" v-else :key="f.prop">
                <span class="ro-label"><i v-if="f.required" class="ro-req">*</i>{{ f.label }}</span>
                <input class="ro-input" v-model="addForm[f.prop]" :placeholder="f.placeholder || ('请输入'+f.label)" />
              </div>
              </template>
            </template>
          </div>
        </div>

          <!-- 调试信息：默认隐藏，仅在 URL 带 ?debug=1 时展示（生产/测试环境均不再暴露） -->
          <div class="ro-debug" v-if="debugOn && saveDebug">{{ saveDebug }}</div>
          <div class="ro-actions">
            <button type="button" class="ro-btn ro-btn--cancel" @click="closeForm">取消</button>
            <!-- 【P12】支付单双动作：存草稿 / 提交审批；其他单据保持单保存 -->
            <template v-if="isPaymentForm">
              <button type="button" class="ro-btn ro-btn--cancel" :disabled="saving" @click="submitAdd('draft')">存草稿</button>
              <button type="button" class="ro-btn ro-btn--save" :disabled="saving" @click="submitAdd('submit')">
                {{ saving ? '提交中…' : '提交审批' }}
              </button>
            </template>
            <!-- 【迭代优化】非支付单单据（会议室/增值/回款计划等）：「保存并继续」
                 保存后表单不关闭、**内容全部保留**，可改一处再录下一单。支付单保持「存草稿 / 提交审批」双动作不变。 -->
            <template v-else>
              <button type="button" class="ro-btn ro-btn--cont" :disabled="saving" @click="submitAdd('continue')">
                {{ saving ? '保存中…' : '保存并继续' }}
              </button>
              <button type="button" class="ro-btn ro-btn--save" :disabled="saving" @click="submitAdd()">
                {{ saving ? '保存中…' : '保存' }}
              </button>
            </template>
          </div>
        </div>
      </van-popup>
    </div>

    <!-- 【P108】筛选：滚轮日期选择器 -->
    <van-popup v-model="filterDateShow" round position="bottom">
      <van-datetime-picker
        type="date"
        v-model="filterDateValue"
        :title="filterDateTitle"
        @confirm="onFilterDateConfirm"
        @cancel="filterDateShow=false"
      />
    </van-popup>

    <!-- 【P108】筛选：remote（客户/商机/合同）选项列表 -->
    <van-action-sheet
      v-model="filterRemoteSheetVisible"
      :title="filterRemoteTitle"
      :actions="filterRemoteActions"
      close-on-click-action
      @select="onFilterRemoteSelect"
    />

    <!-- 下拉选择弹层 -->
    <van-action-sheet
      v-model="sheetVisible"
      :title="sheetTitle"
      :actions="sheetOptions"
      close-on-click-action
      @select="onSheetSelect"
    />

    <!-- 【P105】审批列表：驳回原因弹层（必填） -->
    <van-popup v-model="approvalRejectShow" round position="bottom" class="apx-reject">
      <div class="apx-reject__body">
        <div class="apx-reject__title">驳回审核</div>
        <div class="apx-reject__tip">
          将对 <b>{{ approvalRejectTargets.length }}</b> 条审核执行「驳回」，请填写原因
          <em>（将随通知发送给提交人）</em>
        </div>
        <van-field v-model="approvalRejectRemark" type="textarea" rows="3" maxlength="100" show-word-limit
                   placeholder="如：金额与凭证不符，请重新上传" />
        <div class="apx-reject__btns">
          <van-button block round @click="approvalRejectShow = false">取消</van-button>
          <van-button block round type="danger" :loading="approvalSubmitting" @click="submitApprovalReject">确认驳回</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 【P106】审批列表：通过确认弹层
         ★ 真机故障修复：原「通过」用 $dialog.confirm（Vant 全局函数式弹层），在真机上
         会被页面内已有 popup / 固定底栏盖住或根本不弹 → 用户点「通过」毫无反应。
         现改为与「驳回」完全同构的底部 van-popup（该链路真机已验证可用），并互斥。 -->
    <van-popup v-model="approvalPassShow" round position="bottom" class="apx-reject">
      <div class="apx-reject__body">
        <div class="apx-reject__title">审核通过</div>
        <div class="apx-reject__tip">
          将对 <b>{{ approvalPassTargets.length }}</b> 条审核执行「通过」。
          <em>通过后会议室订单生效、档期生效，销售合同审核状态同步回写。不可撤销。</em>
        </div>
        <div class="apx-approve__list" v-if="approvalPassPreview.length">
          <div class="apx-approve__item" v-for="(t, i) in approvalPassPreview" :key="i">{{ t }}</div>
          <div class="apx-approve__more" v-if="approvalPassTargets.length > approvalPassPreview.length">
            等共 {{ approvalPassTargets.length }} 条
          </div>
        </div>
        <div class="apx-reject__btns">
          <van-button block round @click="approvalPassShow = false">取消</van-button>
          <van-button block round type="primary" :loading="approvalSubmitting" @click="submitApprovalPass">确认通过</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 单据列表排序 -->
    <van-action-sheet
      v-model="billSortSheetVisible"
      title="排序方式"
      :actions="billSortOptions"
      close-on-click-action
      @select="onBillSortSelect"
    />

    <!-- 【P24-5/7】支付单筛选面板：支付时间 / 支付方式 / 支付类型 / 支付类型明细 / 状态 / 受理人 -->
    <van-popup v-model="billFilterSheetVisible" round position="bottom" :style="{ height: '76%' }">
      <div class="cd-form">
        <div class="cd-form__bar">
          <span class="cd-form__title">筛选</span>
          <van-icon name="cross" class="cd-form__close" @click="billFilterSheetVisible=false" />
        </div>
        <div class="cd-form__body">
          <div class="bf-group" v-for="f in (billTab && billTab.filters) || []" :key="f.prop">
            <div class="bf-label">{{ f.label }}</div>
            <!-- 【P108】日期区间：改为滚轮选择器（原为原生 input[type=date]，移动端体验差） -->
            <div v-if="f.type === 'daterange'" class="bf-range">
              <div class="bf-input bf-input--pick" :class="{ 'bf-placeholder': !billFilter[f.beginKey] }"
                   @click="openFilterDate(f.beginKey, f.label + '（开始）')">
                {{ billFilter[f.beginKey] || '开始日期' }}
              </div>
              <span class="bf-sep">至</span>
              <div class="bf-input bf-input--pick" :class="{ 'bf-placeholder': !billFilter[f.endKey] }"
                   @click="openFilterDate(f.endKey, f.label + '（结束）')">
                {{ billFilter[f.endKey] || '结束日期' }}
              </div>
            </div>
            <!-- 下拉（action sheet：支持内联 options 与 optionsFn 字典） -->
            <div v-else-if="f.type === 'select'" class="bf-select" @click="openFilterOption(f)">
              <span :class="{ 'bf-placeholder': !filterOptionText(f) }">{{ filterOptionText(f) || ('请选择' + f.label) }}</span>
              <van-icon name="arrow-down" />
            </div>
            <!-- 【P108】可搜索选择器（客户/商机/销售合同/财务合同）：点击弹出选项列表 -->
            <div v-else-if="f.type === 'remote'" class="bf-select" @click="openFilterRemote(f)">
              <span :class="{ 'bf-placeholder': !filterOptionText(f) }">{{ filterOptionText(f) || (f.placeholder || ('请选择' + f.label)) }}</span>
              <van-icon name="arrow-down" />
            </div>
            <!-- 关键字 -->
            <input v-else-if="f.type === 'search'" class="bf-input bf-input--full" type="text"
              v-model="billFilter[f.prop]" :placeholder="f.placeholder || '请输入关键字'" />
          </div>
          <div class="bf-actions">
            <div class="bf-btn bf-btn--ghost" @click="onBillFilterReset">重置</div>
            <div class="bf-btn bf-btn--primary" @click="onBillFilterConfirm">确定</div>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- ============================================================
         【P206】会议室订单筛选面板（与「快捷入口 → 会议室订单」1:1 同款）
         枚举 → chips 药丸（首项「全部」）／日期 → 区间滚轮／
         客户·商机·销售合同·财务合同 → 可搜索选择器（› 整行）
         ============================================================ -->
    <bill-filter-panel
      v-if="billTab && billTab.key === 'room'"
      v-model="roomFilterShow"
      :filters="billTab.filters"
      :applied="billFilter"
      :title="'筛选' + (billTab ? billTab.title : '')"
      :bill-type="billTab ? billTab.key : ''"
      api-prefix="client"
      @apply="onRoomFilterApply"
    />

    <!-- 【P24-5】筛选下拉：选项选择 -->
    <van-action-sheet
      v-model="filterPickSheetVisible"
      :title="filterPickLabel"
      :actions="filterPickActions"
      close-on-click-action
      @select="onFilterPickSelect"
    />

    <!-- 日期选择器 -->
    <van-popup v-model="dateVisible" position="bottom">
      <van-datetime-picker
        v-model="dateCurrent"
        type="date"
        :min-date="minDate"
        @confirm="onDateConfirm"
        @cancel="dateVisible=false"
      />
    </van-popup>

    <!-- 【P33】使用时间选择器（滑动，替代裸文本输入）默认 08:30 / 20:30 -->
    <van-popup v-model="timeVisible" position="bottom" round>
      <van-datetime-picker
        v-model="timeCurrent"
        type="time"
        title="选择使用时间"
        @confirm="onTimeConfirm"
        @cancel="timeVisible=false"
      />
    </van-popup>

    <!-- 编辑合同 -->
    <van-popup v-model="editVisible" round position="bottom" :style="{ height: '88%' }">
      <div class="cd-form">
        <div class="cd-form__bar">
          <span class="cd-form__title">编辑合同</span>
          <van-icon name="cross" class="cd-form__close" @click="editVisible=false" />
        </div>
        <div class="cd-form__body">
          <van-form @submit="submitEdit">
            <van-field v-model="editForm.name" label="合同名称" placeholder="请输入合同名称" required />
            <van-field v-model="editForm.serial_number" label="合同编号" placeholder="留空自动生成" />
            <van-field v-model="editForm.sales_amount" label="实际销售金额" type="number" placeholder="请输入实际销售金额" />
            <van-field v-model="editForm.room_amount" label="会议室金额" type="number" placeholder="请输入会议室金额" />
            <van-field v-model="editForm.value_added_services_a_amount" label="订餐金额" type="number" placeholder="请输入订餐金额" />
            <van-field v-model="editForm.value_added_services_b_amount" label="茶歇金额" type="number" placeholder="请输入茶歇金额" />
            <van-field v-model="editForm.other_amount" label="其他金额" type="number" placeholder="请输入其他金额" />
            <van-field v-model="editForm.signing_date" label="签订日期" placeholder="YYYY-MM-DD" />
            <van-field v-model="editForm.start_date" label="开始时间" placeholder="YYYY-MM-DD" />
            <van-field v-model="editForm.end_date" label="结束时间" placeholder="YYYY-MM-DD" />
            <van-field v-model="editForm.contacts" label="联系人" placeholder="请输入联系人" />
            <van-field v-model="editForm.phone" label="手机" placeholder="请输入手机号" />
            <van-field v-model="editForm.weixin" label="微信" placeholder="请输入微信号" />
            <van-field v-model="editForm.remark" label="备注" type="textarea" rows="3" placeholder="选填" />
          </van-form>
        </div>
        <div class="cd-form__actions">
          <van-button block plain @click="editVisible=false">取消</van-button>
          <van-button block type="info" native-type="submit" :loading="saving" loading-text="保存中">保存</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 删除确认（全站统一版：ConfirmDialog 居中弹窗） -->
    <ConfirmDialog
      v-model="showDel"
      :desc="delDialogDesc"
      :loading="saving"
      loading-text="删除中"
      @confirm="confirmDelete"
    />

    <!-- 【P108f】合同详情：删除确认（与商机详情同款，全站统一 ConfirmDialog） -->
    <ConfirmDialog
      v-model="contractDelShow"
      :desc="'确定要删除「' + ((contract && contract.name) || '') + '」吗？删除后不可恢复，审批记录与回款计划会一并作废。'"
      :loading="contractDeleting"
      loading-text="删除中"
      @confirm="confirmDeleteContract"
    />

    <!-- 全站统一轻提示（替代散落的 Vant $toast / Element Message） -->
    <CrmNotice ref="notice" />

  </div>
</template>

<script>
import contractDetailMixin from "../mixins/contractDetailMixin.js";
import { ImagePreview } from "vant";
// 【P24-5】支付单筛选：受理人下拉
// 【P95】三.00 销售合同审批：通过 / 驳回；三.1.01 合同删除
import { getPaymentOwnerOptions, deleteOrdersData, contractApprovalDoData, contractApprovalBadgeData, roomOrderRejectBadgeData, getBillFilterOptionsData, isManagerData, getContractRoomFormOptionsData } from "../api/api.js";
import { suppressGlobalError } from "../util/request.js";
// 单据页面配置聚合出口 + 合同阶段映射（1待执行 2已预定 3已支付 4已全款）
import {
  CONTRACT_STAGES,
  stageIndex,
  stageLabel,
  stagePercent
} from "../pages/contract/index.js";

// 合同详情字段分组：对齐后台 admin/CRMOrdersController V5 详情配置
// 【P24-2】按业务 22 项对照表严格重排 money 字段（含 4 组重复口径，是业务有意为之：
//   前 9 项是「本单口径」，第 13-16 项是「订单口径」，同名不同来源，需全部展示）。
//   注意「充值金额」正确字段是 total_deposits，旧值 recharge_amount 已废弃。
const FIELD_GROUPS = [
  {
    key: "biz",
    title: "业务信息",
    money: [
      /* 【P97 三.1.02 + 去重修复】原实现 22 项里有 4 组「同一个 prop 出现两次」的低级重复
         （payment_amount / pending_payment_amount / total_deposits / balance_amount 各写两遍），
         详情页会把同名字段渲染成两格，看起来就是「页面错了」。此处按业务口径去重为 18 项：
           ① 销售侧构成：实际销售金额 / 会议室 / 订餐 / 茶歇 / 其他
           ② 订单口径：订单总金额 / 订单支付金额(total_payment_amount，需求指定字段) / 订单退款金额
           ③ 收付口径：非余额支付 / 余额支付 / 支付金额 / 待支付金额
           ④ 余额与退款：充值金额 / 当前余额 / 退款现金给客户 / 退款到订单的余额 / 转移入 / 转移出 */
      { label: "实际销售金额", prop: "sales_amount" },
      { label: "会议室金额", prop: "room_amount" },
      { label: "订餐金额", prop: "value_added_services_a_amount" },
      { label: "茶歇金额", prop: "value_added_services_b_amount" },
      { label: "其他金额", prop: "other_amount" },
      { label: "订单总金额", prop: "total_amount" },
      { label: "订单支付金额", prop: "total_payment_amount" },
      { label: "订单退款金额", prop: "financial_refun_amount" },
      { label: "非余额支付", prop: "base_payment_amount" },
      { label: "余额支付", prop: "recharge_payment_amount" },
      { label: "支付金额", prop: "payment_amount" },
      { label: "待支付金额", prop: "pending_payment_amount" },
      { label: "充值金额", prop: "total_deposits" },
      { label: "当前余额", prop: "balance_amount" },
      { label: "退款现金给客户", prop: "financial_refun_cash_amount" },
      { label: "退款到订单的余额", prop: "financial_refun_balance_amount" },
      { label: "转移来的余额", prop: "financial_transfer_in_balance_amount" },
      { label: "转移出的余额", prop: "financial_transfer_out_balance_amount" }
    ],
    rows: [
      { label: "关联商机", prop: "opportunities_name" },
      { label: "签订日期", prop: "signing_date" },
      { label: "开始时间", prop: "start_date" },
      { label: "结束时间", prop: "end_date" },
      { label: "联系人", prop: "contacts" },
      { label: "手机", prop: "phone" },
      { label: "微信", prop: "weixin" },
      { label: "备注", prop: "remark", strip: true }
    ]
  },
  {
    key: "sys",
    title: "系统信息",
    money: [],
    rows: [
      { label: "负责人", prop: "owner_name" },
      { label: "创建人", prop: "create_user_name" },
      { label: "更新人", prop: "update_user_name" },
      { label: "创建时间", prop: "create_time" },
      { label: "更新时间", prop: "update_time" }
    ]
  }
];

// 合同详情页：模板与样式保留在此组件，所有业务逻辑下沉到 contractDetailMixin.js
// （可被本组件独立路由使用，也可被 client.vue 以 <contract-detail :contract-sn> 内嵌复用）
// 全站统一删除确认弹窗（居中弹窗），与本页 .cd-del-pop 旧样式二选一，现已统一
import ConfirmDialog from "@/components/ConfirmDialog.vue";
// 全站统一轻提示（02）：替代 Vant $toast / Element Message，保证失败也能展示后端 msg 原文
import CrmNotice from "@/components/CrmNotice.vue";
// 自研富文本编辑器（04）：备注富文本化，输出 HTML，不引第三方库
import RichEditor from "@/components/RichEditor.vue";
/* 【P206】单据筛选面板（通用共用组件）：把「快捷入口」内嵌的 chips 式筛选面板抽出，
   供合同详情内的单据列表复用，保证与快捷入口 1:1（枚举 chips / 区间滚轮 / 可搜索选择器）。 */
import BillFilterPanel from "@/components/BillFilterPanel.vue";

export default {
  name: "ContractDetail",
  components: { ConfirmDialog, CrmNotice, RichEditor, BillFilterPanel },
  mixins: [contractDetailMixin],
  props: {
    // 内嵌场景由父组件传入 sn；独立路由场景不传，由 mixin 读取 $route.params.sn
    contractSn: { type: String, default: "" },
    // 快捷入口某单据 tile 进入时，自动打开对应单据 tab 的 key（如 payment / refund / room）
    pendingBill: { type: String, default: "" },
    /* 【P108】从审核消息跳转过来时，需要定位高亮的会议室订单 sn */
    pendingRoomSn: { type: String, default: "" },
    /* 【P95-B4 四.1.005】快捷入口列表点「编辑」→ 打开合同详情时自动拉起该单据的编辑表单
       （单据都挂在合同下，编辑必须带合同上下文，故从快捷入口「跳」到合同详情内编辑） */
    pendingEditSn: { type: String, default: "" },
    // 内嵌于 client.vue 合同时为 true：编辑统一回退到父级列表编辑弹层，不再使用本组件内联编辑弹窗
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      /* 【P214】多选弹层状态 */
      multiPickShow: false,
      multiPickProp: '',
      multiPickKw: '',
      multiPickSelected: [],
      /* 【P215】补齐：原实现缺这两个声明，弹层标题空白且数据源取不到 */
      multiPickFieldDef: null,
      multiPickOptions: [],
      // 当前打开的独立单据页面（tab.key）；null = 停留在合同总览
      billPage: null,
      /* 【P105】审批列表：批量选择 / 提交 / 驳回弹层 */
      approvalBatch: false,
      approvalSelected: [],
      approvalSubmitting: false,
      approvalRejectShow: false,
      approvalRejectRemark: "",
      approvalRejectTargets: [],
      /* 【P105f】当前登录人「可操作」的待审单 sn（后端按 审批人本人 ∪ join 管理者 计算）——
         用于决定是否渲染【通过】【驳回】按钮，避免提交人/同事看到按钮却点不动（403） */
      approvalMineSns: [],
      /* 【P106】通过确认弹层 + 操作结果横幅（修复真机「点通过没反应」） */
      approvalPassShow: false,
      approvalPassTargets: [],
      approvalResult: { type: '', text: '' },
      /* 【P108f】详情操作条：删除确认弹窗 / 我是否管理人员 */
      contractDelShow: false,
      contractDeleting: false,
      contractDelSn: '',
      canManageDelete: false,
      /* 【P107】会议室订单「被驳回」气泡计数（后端权威口径） */
      roomRejectCount: 0,
      /* 【P107】从审核列表跳转过来时，待定位高亮的会议室订单 sn */
      pendingRoomSn: '',
      // 三组合同字段是否展开（全部默认收起，腾出空间放单据图标）
      groupOpen: { biz: false, sys: false },
      // 单据页下拉刷新中
      billRefreshing: false,
      // 【P24-1】总览区（Hero + 合同信息 + 业务单据宫格）下拉刷新中
      overviewRefreshing: false,
      // 单据列表本地搜索关键字
      billSearch: "",
      // 单据列表排序方式：'' | 'amount-desc' | 'amount-asc' | 'date-desc' | 'date-asc'
      billSort: "",
      // 排序选择弹窗
      billSortSheetVisible: false,
      // 排序选项
      billSortOptions: [],
      // 【P24-5/7】服务端筛选面板（支付单等声明了 filters 的单据使用）
      billFilterSheetVisible: false,
      // 筛选表单值：{ payment_date_begin, payment_date_end, payment_method, payment_type, payment_product, status, owner_id }
      billFilter: {},
      /* 【P108】筛选：滚轮日期选择器状态 */
      filterDateShow: false,
      filterDateValue: new Date(),
      filterDateTitle: '选择日期',
      filterDateField: '',
      /* 【P108】筛选：remote 选择器状态（客户/商机/销售合同/财务合同） */
      filterRemoteSheetVisible: false,
      filterRemoteTitle: '请选择',
      filterRemoteActions: [],
      filterRemoteKind: '',
      filterRemoteProp: '',
      // 筛选下拉字典：{ payment_method:[{value,text}], ... }
      billFilterOptions: {},
      // 筛选条件是否已生效（有值即视为生效，用于按钮高亮）
      billFilterActive: false,
      /* 【P206】会议室订单改用「快捷入口」同款筛选面板（chips/滚轮/可搜索选择器） */
      roomFilterShow: false,
      // 已生效条件的可读文案（渲染「筛选：xxx · yyy」状态条）
      billFilterLabels: [],
      // 【P24-5】筛选下拉的二级选择面板
      filterPickSheetVisible: false,
      filterPickLabel: "",
      filterPickActions: [],
      filterPickProp: "",
      fieldGroups: FIELD_GROUPS,
      // 单据详情（只读弹层）：点列表卡片弹出
      detailShow: false,
      detailRow: null,
      // 【P16】财务合同详情：底部关联区块（会议室/回款单/发票/退款记录）
      detailRelated: [],
      /* 【P95-B4 四.1.005】快捷入口「编辑」联动是否已消费（避免重复弹表单） */
      pendingEditDone: false
    };
  },
  created() {
    // 从快捷入口某单据 tile 进入时，自动打开对应单据 tab
    if (this.pendingBill && this.tabConfigs && this.tabConfigs.some(t => t.key === this.pendingBill)) {
      this.billPage = this.pendingBill;
    }
    // 【P95-B4 四.1.005】带 pendingEditSn 进来时，等该单据列表就绪后自动打开编辑表单
    //   （watch 已覆盖 billPage / lists 两个触发点，并自带重试）
  },
  mounted() {
    /* 【P107】首屏拉一次会议室订单「被驳回」气泡（与各单据列表并行，不阻塞渲染） */
    this.fetchRoomRejectBadge();
    /* 【P108f】拉一次「我是否管理人员」→ 决定详情操作条上「删除」是否显示 */
    isManagerData({}).then(res => {
      if (res && res.code === 200 && res.data) { this.canManageDelete = Number(res.data.is_manager) === 1; }
    }).catch(() => { suppressGlobalError(); });
    /* 【P108】首屏拉一次「审核人待审数」→ 宫格「审批」红气泡；
       并承接「审核消息跳转到会议室订单」的目标 sn（外部 prop 优先，其次 URL query） */
    this.fetchApprovalMine();
    const q = (window.location.search || '') + (window.location.hash || '');
    const m = /(?:room_sn|room)=([A-Za-z0-9]{16,})/.exec(q);
    if (!this.pendingRoomSn && m) { this.pendingRoomSn = m[1]; }
    if (this.pendingRoomSn) {
      const t = (this.tabConfigs || []).find(x => x.key === 'room');
      if (t) { this.$nextTick(() => { this.goRoomOrderTab(this.pendingRoomSn); }); }
    }
  },
  /* 【P95-B4 四.1.005】快捷入口「编辑」联动的两处触发点：
     ① 单据页切换（billPage）→ 等列表加载；② lists 内容变化（异步加载完成）→ 命中即拉起表单。
     仅在 pendingEditSn 存在且尚未消费时生效，避免常态深监听开销。 */
  watch: {
    billPage() {
      this.$nextTick(() => this.tryOpenPendingEdit(0));
      /* 【P105f】无论从宫格点入还是由 pendingBill 直接落到审批页，都拉一次「我可操作」列表 */
      if (this.billPage === 'approval') { this.fetchApprovalMine(); }
      /* 【P107】落到「会议室订单」页且带跳转目标 → 定位高亮该行 */
      if (this.billPage === 'room' && this.pendingRoomSn) { this.tryHighlightRoom(0); }
      /* 【P206】切换单据页 → 清空上一单据的筛选态与条件条，避免条件串到别的单据 */
      this.roomFilterShow = false;
      this.billFilter = {};
      this.billFilterLabels = [];
      this.billFilterActive = false;
    },
    lists: {
      deep: true,
      handler() {
        if (this.pendingEditSn && !this.pendingEditDone) this.tryOpenPendingEdit(0);
      }
    }
  },
  computed: {
    /* 【P105】当前单据页是否为「审批」；以及批量全选态 */
    isApprovalTab() {
      return !!(this.billTab && this.billTab.key === 'approval');
    },
    approvalAllSelected() {
      const enabled = (this.filteredBillRows || [])
        .filter(it => this.approvalRowCanDo(it.row))
        .map(it => it.row.sn);
      if (!enabled.length) return false;
      const sel = this.approvalSelected || [];
      return enabled.every(sn => sel.indexOf(sn) > -1);
    },
    /* 【P106】审核 tab 概览：待审核行数 / 待我处理行数（用于空权限提示，避免"以为按钮坏了"） */
    approvalPendingRows() {
      return (this.filteredBillRows || []).filter(it => this.approvalRowPending(it.row)).length;
    },
    approvalMineCount() {
      return (this.filteredBillRows || []).filter(it => this.approvalRowCanDo(it.row)).length;
    },
    /* 【P106】通过弹层里展示即将通过的审批内容摘要（最多 3 条） */
    approvalPassPreview() {
      const sns = this.approvalPassTargets || [];
      const out = [];
      for (const it of (this.filteredBillRows || [])) {
        if (sns.indexOf(it.row.sn) > -1) {
          const t = this.approvalRowText(it.row);
          out.push(t.length > 46 ? (t.slice(0, 46) + '…') : t);
          if (out.length >= 3) break;
        }
      }
      return out;
    },
    // 【P95】三.00 待审批信息（后端 ordersDetail 返回在 contract.approval）
    approvalInfo() {
      return (this.contract && this.contract.approval) ? this.contract.approval : null;
    },
    /** 【P97】审批三态：pending(待审批) / pass(已通过) / reject(已驳回) —— 用于审批区配色与文案 */
    approvalState() {
      const a = this.approvalInfo;
      if (!a) return '';
      const st = Number(a.status);
      if (st === 1) return 'pending';
      if (st === 2 || st === 3) return 'pass';
      if (st === 4 || st === 5 || st === 6) return 'reject';
      return '';
    },
    // 【P95】三.1.01 是否可删除该合同（后端 can_delete：仅负责人 / 管理员）
    canDeleteContract() {
      return !!(this.contract && Number(this.contract.can_delete) === 1);
    },
    // 调试条开关：仅在 URL 显式带 ?debug=1 或 hash 内含 debug=1 时展示
    // （默认隐藏，避免把「1. 进入 submitAdd」这类内部状态暴露给门店用户）
    debugOn() {
      const q = (window.location.search || "") + (window.location.hash || "");
      return /[?&]debug=1(&|$)/.test(q);
    },
    // 当前单据页对应的配置对象
    billTab() {
      if (!this.billPage) return null;
      return (this.tabConfigs || []).find(t => t.key === this.billPage) || null;
    },
    // 【P97】三.3.05 单据详情里的「回款计划」行（原来 plans 类型被 detailGroups 直接跳过）
    //   数据来源：列表接口已挂 payment_plans（ClientController:23270 / 24038），
    //   兼容后端把计划挂在行根上（pay_plans）的情况。
    detailPlans() {
      const row = this.detailRow;
      if (!row) return [];
      const arr = row.payment_plans || row.pay_plans || [];
      if (!Array.isArray(arr)) return [];
      return arr
        .filter(p => p && (p.date || (p.amount !== undefined && p.amount !== null && p.amount !== '')))
        .map(p => ({
          date: p.date ? String(p.date).slice(0, 10) : '',
          amount: (p.amount === undefined || p.amount === null || p.amount === '') ? '' : p.amount
        }));
    },
    // 单据详情（只读）：按 group 分组，只渲染有值的字段；金额类字段用 billMoney 格式化
    detailGroups() {
      const row = this.detailRow;
      if (!row) return [];
      const fields = (this.billTab && this.billTab.fields) || [];
      const map = {};
      const order = [];
      fields.forEach(f => {
        if (!f || !f.prop) return;
        /* plans（回款计划多行，单独成组）/ hidden 不在只读详情里展示 */
        if (f.type === "hidden" || f.type === "plans") return;
        // 【P13-fix】走 rowDisp：客户/商机/会议室订单等关联字段按 options 反解名称，不再显示 SN/ID
        const v = this.rowDisp(this.billTab, f, row);
        if (v === "" || v === null || v === undefined) return;
        const g = f.group || "信息";
        if (!map[g]) { map[g] = { name: g, fields: [] }; order.push(g); }
        map[g].fields.push({
          label: f.label,
          value: v,
          money: /amount|money|price/i.test(f.prop),
          /* 【P214】upload 类型（支付单图片凭证）渲染缩略图 + 点击大图，不再显示「—」 */
          isImage: f.type === "upload"
        });
      });
      return order.map(k => map[k]);
    },
    // 单据列表：把每行组装成统一结构（标题/金额/状态/单号/日期/详情/计划），9 个单据页共用一套渲染
    billRows() {
      const tab = this.billTab;
      if (!tab) return [];
      const rows = this.lists[tab.key] || [];
      return rows.map(row => {
        const m = this.billMeta(tab, row);
        // 卡片两行摘要：第一行「编号 · 日期」，第二行取前两个详情字段（muted）
        let r1 = "";
        if (m.noText) r1 += "编号：" + m.noText;
        if (m.dateText) r1 += (r1 ? " · " : "") + m.dateText;
        if (!r1) r1 = "—";
        let r2 = (m.details || []).slice(0, 2)
          .map(f => f.label + "：" + this.rowDisp(tab, f, row))
          .join("  ·  ");
        if (!r2) r2 = "点击查看完整详情";
        return {
          row: row,
          title: this.rowTitle(tab, row),
          amountNum: m.amountNum,
          amountText: m.amountText,
          noText: m.noText,
          dateText: m.dateText,
          status: m.status,
          statusClass: this.billStatusClass(m.status),
          details: m.details,
          plans: row.payment_plans || [],
          row1: r1,
          row2: r2
        };
      });
    },
    // 单据列表：本地搜索 + 排序
    // 【P24-5/7】若该单据声明了 filters（如支付单），筛选/关键字由服务端处理，
    // 本地仅保留排序，避免服务端结果被本地二次过滤掉（如 payment_date 区间）。
    filteredBillRows() {
      let arr = (this.billRows || []).slice();
      const tab = this.billTab || {};
      const serverFiltered = !!(tab.filters && tab.filters.length);
      const kw = (this.billSearch || "").trim();
      if (kw && !serverFiltered) {
        const lower = kw.toLowerCase();
        arr = arr.filter(item => {
          const text = [item.title, item.row1, item.row2, item.status].join(" ");
          return text.toLowerCase().indexOf(lower) >= 0;
        });
      }
      const sort = this.billSort || "";
      if (sort === "amount-desc") {
        arr.sort((a, b) => (b.amountNum === null || b.amountNum === undefined ? -Infinity : b.amountNum) - (a.amountNum === null || a.amountNum === undefined ? -Infinity : a.amountNum));
      } else if (sort === "amount-asc") {
        arr.sort((a, b) => (a.amountNum === null || a.amountNum === undefined ? Infinity : a.amountNum) - (b.amountNum === null || b.amountNum === undefined ? Infinity : b.amountNum));
      } else if (sort === "date-desc") {
        arr.sort((a, b) => String(b.dateText || "").localeCompare(String(a.dateText || "")));
      } else if (sort === "date-asc") {
        arr.sort((a, b) => String(a.dateText || "").localeCompare(String(b.dateText || "")));
      }
      return arr;
    },
    // 删除确认弹窗：标题/名称随单据类型变化
    // 【P205 1.1.02.001】兜底文案统一为「会议室订单」
    delTitle() {
      return this.billTab ? this.billTab.title : "会议室订单";
    },
    delName() {
      const r = this.delRow;
      if (!r) return "—";
      if (this.billTab) return this.rowTitle(this.billTab, r);
      return r.meeting_room_name || r.meeting_room_sn || "—";
    },
    // 统一删除确认弹窗的说明文案（全站同模板：确定要删除该「XX」吗？删除后不可恢复。）
    delDialogDesc() {
      return '确定要删除该「' + this.delTitle + '」吗？删除后不可恢复。';
    },
    // 单据页顶部汇总：条数 + 合计金额（有金额字段的单据才显示合计）
    billTotal() {
      let sum = 0;
      let has = false;
      this.billRows.forEach(r => {
        if (r.amountNum !== null && r.amountNum !== undefined) {
          sum += r.amountNum;
          has = true;
        }
      });
      return { count: this.billRows.length, sum: sum, hasSum: has };
    },
    // 基于过滤后的单据列表计算汇总
    filteredBillTotal() {
      let sum = 0;
      let has = false;
      this.filteredBillRows.forEach(r => {
        if (r.amountNum !== null && r.amountNum !== undefined) {
          sum += r.amountNum;
          has = true;
        }
      });
      return { count: this.filteredBillRows.length, sum: sum, hasSum: has };
    },
    stages() {
      return CONTRACT_STAGES;
    },
    stageIdx() {
      return stageIndex(this.contract && this.contract.stage);
    },
    stagePct() {
      return stagePercent(this.contract && this.contract.stage);
    },
    stageText() {
      const c = this.contract || {};
      return stageLabel(c.stage, c.stage_name);
    },

    /* ---------------- 【P223-d】BUG 修复：以下两个**必须是 computed**，不是 method ----------------
       根因：P214/P215 误把它们写进了 methods。模板里当计算属性用
         （`v-for="o in multiPickFiltered"` / `multiPickFiltered.length` / `{{ multiPickTitle }}`），
       实际拿到的是**函数对象**：
         ① `v-for` 迭代函数 → 列表区空白/告警，选项一个都不渲染；
         ② `multiPickFiltered.length` 取到的是**函数参数个数 = 1** ⇒ 头部显示「共 1 项」；
         ③ `multiPickToggleAll()` 里 `const list = this.multiPickFiltered` 拿到函数，
            调 `list.map(...)` 直接抛 **TypeError: list.map is not a function**（点「全选」必崩）。
       模板内不需要加括号调用，故必须落到 computed。 */
    /** 【P215】改用打开时的快照 multiPickOptions（不再现查 tabConfigs，那正是弹层无数据的根因） */
    multiPickFiltered() {
      const all = this.multiPickOptions || [];
      const kw = (this.multiPickKw || "").trim().toLowerCase();
      if (!kw) return all;
      return all.filter(o => String(o.label || "").toLowerCase().indexOf(kw) > -1);
    },
    /** 【P215】弹层标题：优先用快照字段的 label */
    multiPickTitle() {
      return (this.multiPickFieldDef && this.multiPickFieldDef.label) || "选择";
    }
  },
  methods: {
    /* ---------------- 【P214】multiselect 大选项集：弹层可搜索多选 ---------------- */
    /** 选项数超过阈值就走弹层，否则保留原 chips 平铺（避免小选项集也要点两次） */
    /* 【P215】用户要求：直接换成弹层，不再做「>8 条才弹层」的阈值判断 */
    isMultiBig() {
      return true;
    },
    /* 【P215】打开弹层：把字段与选项**快照**下来。
       原实现在计算属性里现查 tabConfigs，一旦当前 tab 不是 payment 就取不到 ⇒ 弹层空。 */
    openMultiPicker(f) {
      this.multiPickProp = f.prop;
      this.multiPickFieldDef = f || null;
      this.multiPickOptions = ((f && f.options) || []).slice();
      this.multiPickSelected = (this.addForm[f.prop] || []).slice();
      this.multiPickKw = "";
      this.multiPickShow = true;
    },
    /* 【P223-d】multiPickFiltered / multiPickTitle 已迁至 computed（见 computed 块内说明） */
    multiPickToggle(v) {
      const i = this.multiPickSelected.indexOf(v);
      if (i > -1) this.multiPickSelected.splice(i, 1);
      else this.multiPickSelected.push(v);
    },
    multiPickToggleAll() {
      const list = this.multiPickFiltered;
      if (this.multiPickSelected.length === list.length && list.length) {
        this.multiPickSelected = [];
      } else {
        this.multiPickSelected = list.map(o => o.value);
      }
    },
    multiPickConfirm() {
      this.$set(this.addForm, this.multiPickProp, this.multiPickSelected.slice());
      this.multiPickShow = false;
    },
    multiPickLabel(f) {
      const sel = this.addForm[f.prop] || [];
      if (!sel.length) return "";
      if (sel.length <= 2) {
        const map = {};
        (f.options || []).forEach(o => { map[o.value] = o.label; });
        return sel.map(v => map[v] || v).join("、");
      }
      return "";
    },

    /* 【P214】图片大图预览：复用 Vant 函数式 ImagePreview（项目已有先例，勿新写 overlay） */
    previewImage(url) {
      if (!url) { return; }
      let list = Array.isArray(url) ? url : [url];
      list = list.map(u => (typeof u === 'string' ? u : (u && (u.url || u.src || u.path)) || ''))
                 .filter(u => !!u);
      if (!list.length) { return; }
      ImagePreview(list);
    },

    /* 【P95】三.1.01 删除销售合同（入口仅负责人可见；后端二次校验归属与下游单据） */
    /* 【P108f】详情操作条：删除合同
       ① 仅管理人员可见/可点（后端 ordersDelete 同口径强校验）
       ② 确认弹窗改用「全站统一 ConfirmDialog」—— 原实现用 $dialog.confirm，
          P106 已实测其真机不弹（等于点了没反应）。 */
    askDeleteContract() {
      if (!this.canManageDelete) { this.notice('只有管理人员可以删除合同', 'warning'); return; }
      const sn = this.contractSn || (this.contract && this.contract.sn) || '';
      if (!sn) { this.notice('缺少合同编号', 'warning'); return; }
      this.contractDelSn = sn;
      this.contractDelShow = true;
    },
    confirmDeleteContract() {
      const sn = this.contractDelSn;
      if (!sn) { return; }
      this.contractDeleting = true;
      deleteOrdersData({ sn: sn }).then(res => {
        this.contractDeleting = false;
        if (res && res.code === 200) {
          this.contractDelShow = false;
          this.notice('已删除', 'success');
          // 内嵌场景通知父级关闭并刷新列表；独立路由场景返回上一页
          this.$emit('deleted', sn);
          if (!this.embedded) { this.$router.back(); }
        } else {
          suppressGlobalError();
          this.notice((res && res.msg) ? res.msg : '删除失败', 'error');
        }
      }).catch(() => {
        this.contractDeleting = false;
        suppressGlobalError();
        this.notice('删除失败', 'error');
      });
    },
    /* ===== 【P105】销售合同审核：就在「审核」单据列表里操作（单一 + 全选批量） ===== */
    /* 切到「审核」单据页（由审批区按钮调用） */
    goApprovalTab() {
      const t = (this.tabConfigs || []).find(x => x.key === 'approval');
      if (t) { this.openBill(t); }
      else { this.notice('未找到「审核」列表入口', 'warning'); }
    },
    /* 该审批行可否操作（P105g 定版）：
       —— 等待审核（status=2，权威字典）
       —— process_id=2（合同审批 / 会议室订单审批共用）
       —— 【P105g】不再限制 source_sn：source_sn 空=合同级、非空=会议室订单/价格级，
          两类都由后端 _contractApprovalApply 分流处理（_roomOrderApprovalApply）
       —— 【P105f】必须是「当前登录人有权处理」的待审单（badge.sns 白名单，否则点了会 403） */
    approvalRowPending(row) {
      return !!row && Number(row.status) === 2;
    },
    /* 【P105g2】审批内容全文（换行展示，不再截断）：
       优先 task_descript（老系统写入的完整审批事由，如
       「【门店】【会议室】【日期】【时段】【销售价格:100】【市场价格1040】【折扣率:0.96】。销售价格低于20%…」），
       兜底用备注 / 审批类型 / 提交人，保证任何一行都有可读内容。 */
    approvalRowText(row) {
      if (!row) { return '（无审批内容）'; }
      const t = row.task_descript ? String(row.task_descript).trim() : '';
      if (t) { return t; }
      const m = row.mssage ? String(row.mssage).trim() : '';
      if (m) { return m; }
      const a = row.process_id_name ? String(row.process_id_name) : '审批';
      return a + '（无事由描述）';
    },
    approvalRowCanDo(row) {
      return this.approvalRowPending(row)
        && Number(row.process_id) === 2
        && (this.approvalMineSns || []).indexOf(row.sn) > -1;
    },
    /* 【P105f】拉取当前登录人可操作的待审单 sn（按本合同过滤） */
    fetchApprovalMine() {
      const sn = this.sn || this.contractSn || (this.contract && this.contract.sn) || '';
      const params = sn ? { order_sn: sn } : {};
      return contractApprovalBadgeData(params).then(res => {
        if (res && res.code === 200 && res.data) {
          this.approvalMineSns = Array.isArray(res.data.sns) ? res.data.sns : [];
        }
      }).catch(() => { suppressGlobalError(); });
    },
    /* 批量选择 */
    toggleApprovalBatch() {
      this.approvalBatch = !this.approvalBatch;
      if (!this.approvalBatch) { this.approvalSelected = []; }
    },
    isApprovalSelected(sn) { return (this.approvalSelected || []).indexOf(sn) > -1; },
    onApprovalSelect(sn, checked) {
      if (checked) {
        if (this.approvalSelected.indexOf(sn) < 0) { this.approvalSelected.push(sn); }
      } else {
        this.approvalSelected = this.approvalSelected.filter(x => x !== sn);
      }
    },
    toggleApprovalRow(row) {
      if (!this.approvalRowCanDo(row)) { this.notice('该审核已处理或不可在此处理', 'warning'); return; }
      this.onApprovalSelect(row.sn, !this.isApprovalSelected(row.sn));
    },
    toggleApprovalSelectAll(val) {
      if (val) {
        this.approvalSelected = (this.filteredBillRows || [])
          .filter(it => this.approvalRowCanDo(it.row))
          .map(it => it.row.sn);
      } else {
        this.approvalSelected = [];
      }
    },
    /* 【P107】业务单据宫格右上角气泡：
       会议室订单走后端权威计数（不受分页影响）；其余单据沿用「驳回/未通过/拒绝」行数口径。 */
    gridBadge(tab) {
      /* 【P109】审批 tab 气泡按「角色」分流（同一位置，两种口径）：
         · 我是审核人 / 管理人员 → **只显示「需要我审核的」数量**（approvalMineSns 白名单待审）
         · 我是提交人（普通销售） → **只显示「被驳回且尚未处理」的数量**
         ★ 提交人口径取「会议室订单的最新状态」(room.status=2)，**不能**用审批单 status=5：
           - 修改型驳回 → 订单 status=2（等用户编辑后重新提交）→ 算
           - 删除型驳回 → 订单已被恢复为 status=1（用户无需再处理）→ **不算**
           - 重新提交后 → 订单回到 status=0 → 自动不算
           （用审批单口径时，删除型驳回的单会长期停在 status=5，导致气泡永远不消失） */
      if (tab && tab.key === 'approval') {
        const mine = (this.approvalMineSns || []).length;
        if (mine > 0) { return mine; }
        return Number(this.roomRejectCount || 0);
      }
      if (tab && tab.key === 'room') {
        return Number(this.roomRejectCount || 0) || this.tabRejectCount(tab);
      }
      return this.tabRejectCount(tab);
    },
    /* 【P107】该行是否为「审核未通过」——用于整行标红（仅看审核状态，不误伤待审） */
    rowRejected(row) {
      return !!row && String(row.approval_status_name || '') === '未通过';
    },
    /* 【P107】拉取「被驳回的会议室订单」计数（气泡） */
    fetchRoomRejectBadge() {
      const sn = this.sn || this.contractSn || (this.contract && this.contract.sn) || '';
      return roomOrderRejectBadgeData(sn ? { order_sn: sn } : {}).then(res => {
        if (res && res.code === 200 && res.data) {
          this.roomRejectCount = Number(res.data.count || 0);
        }
      }).catch(() => { suppressGlobalError(); });
    },
    /* 【P107】从审核列表跳到对应会议室订单：切到「会议室订单」单据页并定位高亮该行 */
    goRoomOrderTab(sourceSn) {
      const t = (this.tabConfigs || []).find(x => x.key === 'room');
      if (!t) { this.setApprovalResult('warn', '未找到「会议室订单」列表入口'); return; }
      this.pendingRoomSn = String(sourceSn || '');
      this.openBill(t);
    },
    /* 列表异步加载 → 定位并高亮目标行（最多重试 10 次，每次 300ms） */
    tryHighlightRoom(retry) {
      const sn = this.pendingRoomSn;
      if (!sn) { return; }
      const n = typeof retry === 'number' ? retry : 0;
      this.$nextTick(() => {
        const el = document.querySelector('.crm-item[data-sn="' + sn + '"]');
        if (!el) {
          if (n < 10) { setTimeout(() => this.tryHighlightRoom(n + 1), 300); }
          else {
            this.pendingRoomSn = '';
            this.setApprovalResult('warn', '未在当前列表中定位到该会议室订单（可能不在第一页），请用搜索或筛选查找');
          }
          return;
        }
        this.pendingRoomSn = '';
        try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) { el.scrollIntoView(); }
        el.classList.add('crm-item--flash');
        setTimeout(() => { el.classList.remove('crm-item--flash'); }, 2400);
      });
    },
    /* 【P107】操作后刷新会议室订单列表 + 驳回气泡（审核结果会回写 room.status 与审核状态标签） */
    refreshRoomSide() {
      try {
        this.reloadBillList({ key: 'room', listFn: (this.tabConfigs.find(x => x.key === 'room') || {}).listFn }, {});
      } catch (e) { /* 列表未挂载时忽略 */ }
      this.fetchRoomRejectBadge();
    },
    setApprovalResult(type, text) {
      this.approvalResult = { type: type, text: text };
      if (this._aprTimer) { clearTimeout(this._aprTimer); this._aprTimer = null; }
      if (type === 'ok') {
        this._aprTimer = setTimeout(() => {
          this.approvalResult = { type: '', text: '' };
          this._aprTimer = null;
        }, 4000);
      }
    },
    clearApprovalResult() {
      if (this._aprTimer) { clearTimeout(this._aprTimer); this._aprTimer = null; }
      this.approvalResult = { type: '', text: '' };
    },
    /* 【P106】打开审核确认弹层：「通过」「驳回」都用底部 van-popup，且互斥
       （原「通过」走 $dialog.confirm，真机不弹/被盖 → 用户点「通过」毫无反应） */
    openApprovalConfirm(kind, sns) {
      const list = (sns || []).slice();
      if (!list.length) { this.setApprovalResult('warn', '请先勾选要审核的记录'); return; }
      this.approvalRejectShow = false;
      this.approvalPassShow = false;
      if (kind === 'reject') {
        this.approvalRejectTargets = list;
        this.approvalRejectRemark = '';
        this.$nextTick(() => { this.approvalRejectShow = true; });
      } else {
        this.approvalPassTargets = list;
        this.$nextTick(() => { this.approvalPassShow = true; });
      }
    },
    /* 单条：通过 / 驳回 */
    approvalDoOne(row, action) {
      const kind = (action === 'reject') ? 'reject' : 'pass';
      const label = (kind === 'reject') ? '驳回' : '通过';
      /* 【P106】不再静默 return —— 每条提前返回都给出可见提示 */
      if (this.approvalSubmitting) { this.setApprovalResult('warn', '上一条审核正在提交，请稍候再点…'); return; }
      if (!this.approvalRowCanDo(row)) {
        const pending = this.approvalRowPending(row);
        this.setApprovalResult('warn', pending
          ? '该审核不由「当前登录人」负责，无法处理（已刷新列表）'
          : '该审核已被处理（当前状态：' + (row.status_name || row.approval_status || '非待审') + '），已刷新列表');
        this.reloadBillList({ key: 'approval', listFn: this.billTab.listFn }, {});
        this.fetchApprovalMine();
        return;
      }
      this.openApprovalConfirm(kind, [row.sn]);
    },
    /* 批量：通过 / 驳回 */
    approvalDoBatch(action) {
      if (this.approvalSubmitting) { this.setApprovalResult('warn', '上一条审核正在提交，请稍候再点…'); return; }
      const sns = (this.approvalSelected || []).slice();
      if (!sns.length) { this.setApprovalResult('warn', '请先勾选要审核的记录（点右上角「批量」进入勾选态）'); return; }
      this.openApprovalConfirm((action === 'reject') ? 'reject' : 'pass', sns);
    },
    /* 【P106】通过弹层「确认通过」 */
    submitApprovalPass() {
      this.execApprovalDo((this.approvalPassTargets || []).slice(), 'pass', '');
    },
    submitApprovalReject() {
      const r = (this.approvalRejectRemark || '').trim();
      if (!r) { this.setApprovalResult('warn', '请填写驳回原因'); return; }
      this.execApprovalDo((this.approvalRejectTargets || []).slice(), 'reject', r);
    },
    /* 统一执行（单条 / 批量同一接口；逐条独立事务，部分失败保留选中可重试） */
    execApprovalDo(sns, action, remark) {
      if (!sns || !sns.length) { return; }
      this.approvalSubmitting = true;
      const __act = (action === 'pass') ? '通过' : '驳回';
      contractApprovalDoData({ approval_sns: sns.join(','), action: action, remark: remark || '' }).then(res => {
        if (res && res.code === 200) {
          const d = res.data || {};
          this.approvalRejectShow = false;
          this.approvalPassShow = false;
          if (Number(d.fail) > 0) {
            const bads = (d.details || []).filter(x => x.status !== 'ok');
            const failSns = bads.map(x => x.sn);
            this.approvalSelected = failSns;
            this.approvalBatch = failSns.length > 0;
            const __msg = '成功 ' + (d.ok || 0) + ' 条 · 失败 ' + d.fail + ' 条：'
              + ((bads[0] && bads[0].msg) ? bads[0].msg : '请重试') + '（已保留勾选可重试）';
            this.notice(__msg, 'warning');
            this.setApprovalResult('err', __msg);
          } else {
            const __ok = '已' + __act + ' ' + (d.ok || 0) + ' 条';
            this.notice(__ok, 'success');
            this.setApprovalResult('ok', __ok);
            this.approvalBatch = false;
            this.approvalSelected = [];
          }
          this.reloadBillList({ key: 'approval', listFn: this.billTab.listFn }, {});
          this.fetchApprovalMine();
          /* 【P154】审批区状态来自 contract.approval（fetchHeader），
             原实现只调 fetchDetail() ⇒ 点完「通过」后审批区仍显示「待审批」。 */
          this.fetchHeader();
          this.fetchDetail();
          /* 【P154】通知父级（client.vue）：本合同的审批结果已变更，
             返回列表时需要重拉合同列表与角标，否则卡片状态与红色气泡会残留。 */
          this.$emit('changed');
          /* 【P107】审核结果会回写会议室订单的 room.status 与审核状态标签 → 同步刷新会议室订单侧 */
          this.refreshRoomSide();
        } else {
          suppressGlobalError();
          this.approvalRejectShow = false;
          this.approvalPassShow = false;
          const __msg = (res && res.msg) ? res.msg : (__act + '失败');
          this.notice(__msg, 'error');
          this.setApprovalResult('err', __act + '失败：' + __msg);
        }
      }).catch((e) => {
        suppressGlobalError();
        this.approvalRejectShow = false;
        this.approvalPassShow = false;
        const __msg = (e && e.msg) ? e.msg : '网络或接口异常，请重试';
        this.notice(__act + '失败：' + __msg, 'error');
        this.setApprovalResult('err', __act + '失败：' + __msg);
      }).finally(() => {
        this.approvalSubmitting = false;
      });
    },
    // 编辑合同：内嵌场景回退到父级（client.vue 列表编辑弹层），独立路由场景走本组件内联编辑
    onEditContractClick() {
      if (this.embedded) {
        this.$emit("edit");
      } else {
        this.editContract();
      }
    },
    /* 【P108】单据页下拉刷新：重新拉全部单据列表
       修复「下拉刷新无效」：原实现的复位只挂在 .then 上，任一环节异常都会让刷新圈
       永远转下去（之后再拉就没反应）；这里改为「结果必定复位 + 3s 兜底 + 可见反馈」。 */
    onBillRefresh() {
      const done = () => { this.billRefreshing = false; };
      Promise.all([
        this.loadAllLists().catch(() => {}),
        this.fetchHeader().catch(() => {}),
        this.fetchApprovalMine().catch(() => {}),
        this.fetchRoomRejectBadge().catch(() => {})
      ]).then(done).catch(done);
      setTimeout(done, 3000);
      return Promise.resolve();
    },
    /* 【P108】总览区下拉刷新：刷新「合同主数据 + 各单据数量 + 气泡」，结果必定复位
       【P154】原实现漏了 fetchHeader() —— 审批区状态来自 contract.approval（fetchHeader），
       只刷 fetchDetail() 时审批状态不会变，用户感受就是「下拉刷新无效」。 */
    onOverviewRefresh() {
      const done = () => {
        this.overviewRefreshing = false;
        this.setApprovalResult('ok', '已刷新');
      };
      Promise.all([
        this.fetchHeader().catch(() => {}),
        this.fetchDetail().catch(() => {}),
        this.loadAllLists().catch(() => {}),
        this.fetchApprovalMine().catch(() => {}),
        this.fetchRoomRejectBadge().catch(() => {})
      ]).then(done).catch(done);
      setTimeout(done, 3000);
      return Promise.resolve();
    },
    // 打开单据排序选择 / 【P24-5】支付单打开服务端筛选面板
    openSortSheet() {
      const tab = this.billTab;
      /* 【P206】会议室订单：改用「快捷入口」同款筛选面板（枚举 chips / 日期区间滚轮 / 可搜索选择器）。
         旧下拉面板在本单据上存在枚举项 label/text 键错配（「是否免费」等下拉文案恒为空、选中后不回显），
         且交互与快捷入口不一致 ⇒ 本单据整体切换到通用组件 BillFilterPanel。 */
      if (tab && tab.key === 'room') {
        this.roomFilterShow = true;
        return;
      }
      if (tab && tab.filters && tab.filters.length) {
        this.loadBillFilterOptions();
        this.billFilterSheetVisible = true;
        return;
      }
      this.billSortOptions = [
        { name: "默认排序", value: "" },
        { name: "金额从高到低", value: "amount-desc" },
        { name: "金额从低到高", value: "amount-asc" },
        { name: "日期从新到旧", value: "date-desc" },
        { name: "日期从旧到新", value: "date-asc" }
      ].map(o => ({ ...o, className: this.billSort === o.value ? "van-action-sheet__item--active" : "" }));
      this.billSortSheetVisible = true;
    },
    // 【P24-5】拉筛选下拉字典（支付方式/支付类型/支付产品/状态/受理人）
    loadBillFilterOptions() {
      const dict = {
        payment_method: [
          { value: 1, text: "收钱吧" }, { value: 2, text: "网银" }, { value: 3, text: "快团团" },
          { value: 4, text: "美团" }, { value: 5, text: "现金" }, { value: 6, text: "余额" }, { value: 7, text: "其他" }
        ],
        payment_type: [
          { value: 1, text: "订金" }, { value: 2, text: "全款" }, { value: 3, text: "中期款" }, { value: 4, text: "尾款" }
        ],
        payment_product: [
          { value: 1, text: "会议室" }, { value: 2, text: "餐饮" }, { value: 3, text: "茶歇" },
          { value: 4, text: "客户充值" }, { value: 5, text: "其他服务" }
        ],
        status: [
          { value: 0, text: "审核中" }, { value: 1, text: "审核通过" }, { value: 2, text: "审核驳回" }, { value: 3, text: "待提交" }
        ],
        /* 【P108】会议室订单「时段」筛选字典 —— 此前缺失导致筛选面板「时段」下拉恒为空 */
        time_type: [
          { value: 1, text: "全天" }, { value: 2, text: "白天" }, { value: 3, text: "上午" },
          { value: 4, text: "下午" }, { value: 5, text: "晚上" }, { value: 7, text: "下午+晚上" }
        ],
        /* 【P227】门店 / 会议室筛选字典。
           数据源：orderRoomFormOptions 已返回 stores（含 store_sn）与 rooms（含 stores=所属门店 sn），
           这里直接复用，避免为筛选再新增后端接口。
           ★ rooms 带 stores 字段 ⇒ 前端可做「选门店 → 会议室只列该门店」二级联动。 */
        stores: [],
        rooms: [],
        owner: []
      };
      this.billFilterOptions = dict;
      // 受理人（财务人员）异步补全，失败不阻断
      getPaymentOwnerOptions()
        .then(res => {
          const arr = (res && (res.data || res)) || [];
          this.$set(this.billFilterOptions, "owner", Array.isArray(arr) ? arr : []);
        })
        .catch(() => {});
      // 【P227】门店 / 会议室选项（含二级联动所需的 stores 归属字段）
      getContractRoomFormOptionsData({})
        .then(res => {
          const d = (res && res.code === 200) ? res.data : null;
          if (!d) return;
          this.$set(this.billFilterOptions, "stores", (d.stores || []).map(r => ({
            value: r.sn, text: r.name || r.sn, sub: r.store_number || ""
          })));
          this.$set(this.billFilterOptions, "rooms", (d.rooms || []).map(r => ({
            value: r.sn, text: r.name || r.sn, sub: r.room_number || "",
            /* 归属门店 sn，用于二级联动过滤 */
            store_sn: r.store_sn || r.stores || ""
          })));
          /* 同时注册 rooms_filtered：筛选面板按 dependsOn='stores_sn' 自动过滤后使用 */
          this.$set(this.billFilterOptions, "rooms_filtered", this.billFilterOptions.rooms);
        })
        .catch(() => {});
    },

    /* 【P227】门店 → 会议室 二级联动：
       未选门店时列全部会议室；选了门店则只列该门店下的会议室。
       切换门店后，若已选中的会议室已不在新范围内，则清空该会议室选中值。 */
    filterRoomOptions() {
      const storeSn = this.billFilter && this.billFilter.stores_sn;
      const all = this.billFilterOptions.rooms || [];
      if (!storeSn) return all;
      return all.filter(o => String(o.store_sn || "") === String(storeSn));
    },
    onFilterStoreChange() {
      const rooms = this.filterRoomOptions();
      const curRoom = this.billFilter && this.billFilter.room_sn;
      if (curRoom && !rooms.some(o => String(o.value) === String(curRoom))) {
        this.$set(this.billFilter, "room_sn", "");
      }
    },
    /* 【P108】筛选：滚轮日期选择 */
    openFilterDate(field, title) {
      this.filterDateField = field;
      this.filterDateTitle = title || '选择日期';
      const cur = this.billFilter[field];
      this.filterDateValue = cur ? new Date(String(cur).replace(/-/g, '/')) : new Date();
      this.filterDateShow = true;
    },
    onFilterDateConfirm() {
      this.filterDateShow = false;
      const d = new Date(this.filterDateValue);
      const p = n => String(n).padStart(2, '0');
      const str = d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
      if (this.filterDateField) { this.$set(this.billFilter, this.filterDateField, str); }
    },
    /* 【P108】筛选：remote（客户/商机/销售合同/财务合同）选项 */
    openFilterRemote(f) {
      const kind = f.kind || '';
      if (!kind) { this.notice('该筛选项未配置数据源', 'warning'); return; }
      this.filterRemoteProp = f.prop;
      this.filterRemoteTitle = f.label || '请选择';
      this.filterRemoteKind = kind;
      const cur = this.billFilter[f.prop];
      const cached = this.billFilterOptions[kind];
      const render = (list) => {
        const arr = Array.isArray(list) ? list : [];
        this.filterRemoteActions = arr.length
          ? arr.map(o => ({
              name: (o.text || o.name || '') + (o.sub ? '（' + o.sub + '）' : ''),
              value: o.value !== undefined ? o.value : o.sn,
              className: String(cur) === String(o.value !== undefined ? o.value : o.sn) ? 'van-action-sheet__item--active' : ''
            }))
          : [{ name: '暂无可选项', value: '', disabled: true }];
        this.filterRemoteSheetVisible = true;
      };
      if (cached && cached.length) { render(cached); return; }
      getBillFilterOptionsData({ kind: kind }).then(res => {
        const list = (res && res.data && res.data.list) || [];
        this.$set(this.billFilterOptions, kind, list);
        render(list);
      }).catch(() => { suppressGlobalError(); render([]); });
    },
    onFilterRemoteSelect(item) {
      if (item && item.disabled) { return; }
      const p = this.filterRemoteProp;
      if (p) { this.$set(this.billFilter, p, (item && item.value) || ''); }
      this.filterRemoteSheetVisible = false;
    },
    // 【P24-5】筛选面板「确定」：写回 billFilter 并重拉列表
    onBillFilterConfirm() {
      const f = this.billFilter || {};
      const has = Object.keys(f).some(k => f[k] !== "" && f[k] !== null && f[k] !== undefined);
      this.billFilterActive = has;
      this.billFilterSheetVisible = false;
      const extra = {};
      Object.keys(f).forEach(k => { if (f[k] !== "" && f[k] !== null && f[k] !== undefined) extra[k] = f[k]; });
      this.reloadBillList(this.billTab, extra);
    },
    // 【P24-5】筛选面板「重置」
    onBillFilterReset() {
      this.billFilter = {};
      this.billFilterLabels = [];
      this.billFilterActive = false;
      this.reloadBillList(this.billTab, {});
    },
    /* ============================================================
     * 【P206】会议室订单筛选（通用组件 BillFilterPanel 回调）
     *   payload 的键即后端筛选参数（on_site_time_begin / time_type / is_free /
     *   customer_sn / opportunitie_sn / parent_sn / contracts_sn …），直接透传重拉。
     * ============================================================ */
    onRoomFilterApply(payload, labels) {
      this.billFilter = payload || {};
      /* 【P227】门店变更后，若已选会议室不属于该门店，则一并清空，
         否则会把「A 店的会议室」与「B 店」组合成一个查不到结果的筛选条件。 */
      if (this.billFilter.stores_sn && this.billFilter.room_sn) {
        const rooms = this.filterRoomOptions();
        if (!rooms.some(o => String(o.value) === String(this.billFilter.room_sn))) {
          this.$set(this.billFilter, "room_sn", "");
        }
      }
      this.billFilterLabels = Array.isArray(labels) ? labels : [];
      this.billFilterActive = this.billFilterLabels.length > 0;
      this.reloadBillList(this.billTab, this.billFilter);
    },
    /* 【P206】一键清空已生效条件（条件条右侧「清空」） */
    onRoomFilterClear() {
      this.billFilter = {};
      this.billFilterLabels = [];
      this.billFilterActive = false;
      this.reloadBillList(this.billTab, {});
    },
    // 【P24-7】关键字搜索：透传后端 searchText（支付单走 search_key LIKE），并保留本地即时过滤
    onBillSearchSubmit() {
      const tab = this.billTab;
      if (!tab || !tab.filters) return;
      const kw = (this.billSearch || "").trim();
      this.reloadBillList(tab, kw ? { searchText: kw } : {});
    },
    // 【P24-5】打开筛选下拉的二级选择面板
    openFilterOption(f) {
      /* 【P108】优先用内联 options（如「是否免费」），再退回 optionsFn 字典 */
      const src = (Array.isArray(f.options) && f.options.length)
        ? f.options
        : ((f.optionsFn && this.billFilterOptions[f.optionsFn]) || []);
      this.filterPickProp = f.prop;
      this.filterPickLabel = f.label;
      const cur = this.billFilter[f.prop];
      this.filterPickActions = src.map(o => ({
        name: o.text,
        value: o.value,
        className: String(cur) === String(o.value) ? "van-action-sheet__item--active" : ""
      }));
      this.filterPickSheetVisible = true;
    },
    // 【P24-5】筛选下拉当前展示文本
    filterOptionText(f) {
      const v = this.billFilter[f.prop];
      if (v === "" || v === null || v === undefined) return "";
      const src = (Array.isArray(f.options) && f.options.length)
        ? f.options
        : ((f.optionsFn && this.billFilterOptions[f.optionsFn]) || []);
      const hit = src.find(o => String(o.value) === String(v));
      return hit ? hit.text : String(v);
    },
    // 【P24-5】选中筛选下拉值
    onFilterPickSelect(item) {
      const p = this.filterPickProp;
      if (p) this.$set(this.billFilter, p, item.value);
      this.filterPickSheetVisible = false;
    },
    // 选中排序方式
    onBillSortSelect(item) {
      this.billSort = item.value || "";
      this.billSortSheetVisible = false;
    },
    // 三组字段独立展开/收起
    toggleGroup(key) {
      this.$set(this.groupOpen, key, !this.groupOpen[key]);
    },
    // 回款计划是否为「当前必填」：字段声明 requiredWhen='notFree' 时，仅当本单非免费才显示星标
    // （免费单后端不校验回款计划，前端也不应误导用户去填）
    isPlanRequired(f) {
      if (!f) return false;
      if (f.required) return true;
      if (f.requiredWhen !== "notFree") return false;
      return String(this.addForm.is_free) !== "是";
    },
    // 金额字段：空值显示 —，否则 ¥ + 千分位
    moneyText(prop) {
      const c = this.contract || {};
      const v = c[prop];
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (isNaN(n)) return String(v);
      return "¥" + this.fmtMoney(n);
    },
    // 文本字段
    textOf(prop) {
      const c = this.contract || {};
      const v = c[prop];
      if (v === null || v === undefined || v === "") return "—";
      return String(v);
    },
    // 富文本（备注）去标签
    plainText(v) {
      if (v === null || v === undefined) return "—";
      const s = String(v)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return s || "—";
    },
    // 点击宫格图标 → 打开该单据的独立页面（client 内切换，不跳路由）
    openBill(tab) {
      this.billPage = tab.key;
      this.billFormMode = null;
      /* 【P105f】进入审批 tab 时拉取「我可操作」的待审单 */
      if (tab && tab.key === 'approval') { this.fetchApprovalMine(); }
      /* 【P107】进入「会议室订单」页时刷新驳回气泡与列表（审核结果会回写 room.status） */
      if (tab && tab.key === 'room') { this.fetchRoomRejectBadge(); this.tryHighlightRoom(0); }
    },
    closeBill() {
      this.billPage = null;
      this.billFormMode = null;
    },
    // 底部弹窗表单：遮罩/关闭时同步 billFormMode（v-model 手写形式，避免直接改 mixin 状态）
    onFormPopupInput(v) {
      if (!v) this.closeForm();
    },
    // 点列表卡片 → 打开只读详情（编辑走卡片上的「编辑」按钮）
    openRowDetail(row) {
      if (!row) return;
      this.detailRow = row;
      this.detailRelated = [];
      this.detailShow = true;
      // 【P16】财务合同：详情聚合接口（基本信息补全 + 关联会议室/回款单/发票/退款记录）
      const tab = this.billTab;
      if (tab && typeof tab.detailFn === "function") {
        tab.detailFn({ sn: row.sn }).then(res => {
          const d = (res && (res.data || res)) || {};
          const info = d.info || {};
          this.detailRow = Object.assign({}, row, info);
          this.detailRelated = (tab.related || []).map(r => {
            const found = (d.related || []).find(x => x.key === r.key);
            return { key: r.key, label: r.label, items: (found && found.items) || [] };
          });
        }).catch(() => {});
      }
    },
    // 详情页底部「编辑」：关掉详情，直接进编辑表单
    onDetailEdit() {
      const row = this.detailRow;
      this.detailShow = false;
      this.detailRow = null;
      if (row) this.openForm("edit", row);
    },
    // 列表卡片 / 「详情」按钮 / 「编辑」按钮 → 打开整页表单（add=新增空白，edit=回填当前行）
    openForm(mode, row) {
      if (mode === "add") {
        this.openAdd(this.billTab);
      } else {
        this.openEdit(row);
      }
    },
    // 【P95-B4 四.1.005】快捷入口「编辑」联动：找到目标行 → 打开编辑表单（一次即消费，避免重复弹）
    //   trial = 已重试次数（列表异步加载，可能晚于 watcher 触发）
    tryOpenPendingEdit(trial) {
      const n = Number(trial || 0);
      if (!this.pendingEditSn || this.pendingEditDone) return;
      const key = this.billPage;
      if (!key) {
        if (n < 12) setTimeout(() => this.tryOpenPendingEdit(n + 1), 200);
        return;
      }
      const rows = (this.lists && this.lists[key]) || [];
      if (!rows.length) {
        if (n < 12) setTimeout(() => this.tryOpenPendingEdit(n + 1), 200);
        return;
      }
      const row = rows.find(r => String(r.sn) === String(this.pendingEditSn));
      this.pendingEditDone = true;
      if (!row) {
        this.notice('未在该合同下找到该单据，可能已删除或不属于本合同', 'warning');
        return;
      }
      if (!this.rowEditable(row)) {
        this.notice('该单据当前状态不可编辑（审核中 / 已通过）', 'warning');
        return;
      }
      this.openForm('edit', row);
    },
    // 关闭表单页 → 退回列表
    closeForm() {
      this.billFormMode = null;
    },
    // 顶部导航返回：表单页 → 退回列表；单据页 → 退回合同总览；否则关闭整个详情
    onNavLeft() {
      if (this.billFormMode) {
        this.closeForm();
      } else if (this.billTab) {
        this.closeBill();
      } else {
        this.goBack();
      }
    }
  }
};
</script>

<style scoped>
/* 【P24-1】总览区下拉刷新容器：清零内边距，保证 Hero 仍是通栏（否则会被 Vant 默认 padding 挤窄） */
.cd-page > .van-pull-refresh {
  padding: 0;
  margin: 0;
}
.cd-page {
  position: relative;
  /* 与 client-root 一致：扣掉顶栏 + 底部 tabbar 占位，避免整页多余滚动 */
  min-height: calc(100vh - 115px);
  background: #f5f6f8;
  padding-bottom: 24px;
}

/* 顶部导航（白底黑字） */
.cd-nav {
  background: #fff;
}
.cd-nav ::v-deep .van-nav-bar__title,
.cd-nav ::v-deep .van-icon,
.cd-nav ::v-deep .van-nav-bar__text {
  color: #1d2129;
}
.cd-nav ::v-deep .van-nav-bar__content {
  background: #fff;
}
.cd-nav ::v-deep .van-hairline--bottom::after {
  border-color: #f2f3f5;
}

/* Hero 头部 */
.cd-hero {
  background: linear-gradient(135deg, #2b5fd9 0%, #4a8af0 100%);
  padding: 18px 18px 22px;
  color: #fff;
}
/* 主金额：单独一行放大 */
.cd-hero__main {
  margin-bottom: 16px;
}
.cd-hero__main-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
}
.cd-hero__main-value {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
  margin-top: 4px;
  color: #fff;
  word-break: break-all;
}
/* 四个金额指标 */
.cd-hero__metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 12px 4px;
}
.cd-metric {
  text-align: center;
  padding: 0 2px;
}
.cd-metric__label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  white-space: nowrap;
}
.cd-metric__value {
  font-size: 14px;
  font-weight: 700;
  margin-top: 4px;
  color: #fff;
}

/* 信息面板（上浮卡片） */
.cd-info {
  margin: -12px 12px 0;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px 16px;
  box-shadow: 0 4px 14px rgba(31, 58, 110, 0.08);
  position: relative;
}
.cd-info__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}
.cd-info__item {
  display: flex;
  flex-direction: column;
}
.cd-info__label {
  font-size: 12px;
  color: #969799;
  margin-bottom: 3px;
}
.cd-info__value {
  font-size: 14px;
  color: #323233;
  font-weight: 500;
  word-break: break-all;
}
.cd-info__item--full {
  grid-column: 1 / -1;
}
.cd-edit-btn {
  margin-top: 14px;
  width: 100%;
  border-color: #2b5fd9;
  color: #2b5fd9;
  border-radius: 8px;
}

/* 加载：全站统一上下 + 左右居中 */
.cd-loading {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 资料 Tab 紧凑信息列表 */
.cd-info-list {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  overflow: hidden;
  margin-top: 4px;
}
.cd-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  min-height: 42px;
  border-bottom: 1px solid #f2f3f5;
}
.cd-info-row:last-child {
  border-bottom: none;
}
.cd-info-row__label {
  font-size: 13px;
  color: #646566;
}
.cd-info-row__value {
  font-size: 13px;
  color: #323233;
  text-align: right;
  flex: 1;
  margin-left: 12px;
  word-break: break-all;
}

.cd-tabs ::v-deep .van-tab {
  padding: 0 16px;
  flex: 0 0 auto;
}
.cd-page .van-cell-group {
  margin-top: 12px;
}

/* Tab 内容 */
.tab-body {
  padding: 12px;
}

/* Tab 工具条：对齐 client.vue customer-header（返回 + 搜索 + 筛选 + 新增） */
.cd-tabbar {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 10px 14px 8px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(31, 58, 110, 0.05);
}
.cd-tabbar__top {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cd-tabbar__left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  cursor: pointer;
}
.cd-tabbar__back {
  font-size: 18px;
  color: #2b5fd9;
}
.cd-tabbar__title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cd-tabbar__search {
  flex: 1;
  min-width: 0;
  position: relative;
}
.cd-tabbar__search input {
  width: 100%;
  height: 34px;
  line-height: 34px;
  border-radius: 17px;
  border: none;
  background: #f5f6f8;
  font-size: 13px;
  padding: 0 28px 0 32px;
  box-sizing: border-box;
  outline: none;
}
.cd-tabbar__search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #c0c4cc;
}
.cd-tabbar__search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: #c0c4cc;
  cursor: pointer;
}
.cd-tabbar__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.cd-tabbar__btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  color: #1d2129;
  cursor: pointer;
}
.cd-tabbar__btn .van-icon {
  font-size: 18px;
  color: #4e5969;
}
.cd-tabbar__btn--primary .van-icon {
  color: #2b5fd9;
}
/* 【P24-5】筛选已生效：高亮 */
.cd-tabbar__btn--active,
.cd-tabbar__btn--active .van-icon {
  color: #2b5fd9;
}
.cd-tabbar__summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 13px;
  color: #4e5969;
}
/* 【P206】已生效筛选条件条：与「快捷入口」同款（蓝底浅色块 + 文案 + 清空）。
   ⚠️ 基础样式在 client.vue（非 scoped 全局），本页为 scoped 样式，必须在此自带一份，
   否则从深链直接进入本页时条件条会没有样式。 */
.filter-status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0 0;
  padding: 7px 12px;
  background: #eef3ff;
  border-radius: 10px;
  font-size: 13px;
}
.filter-status-bar__txt { color: #2b5fd9; font-weight: 500; }
.filter-status-bar__clear { color: #86909c; cursor: pointer; }
.filter-status-bar__clear:active { opacity: .6; }

/* 弹层 */
.cd-form {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.cd-form__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #f2f3f5;
}
.cd-form__title {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}
.cd-form__close {
  font-size: 20px;
  color: #969799;
}
.cd-form__body {
  flex: 1;
  overflow-y: auto;
  padding: 4px 4px 0;
}
/* 【P24-5】支付单筛选面板 */
.bf-group {
  padding: 12px 16px 4px;
}
.bf-label {
  font-size: 13px;
  color: #646566;
  margin-bottom: 8px;
}
.bf-input {
  flex: 1;
  min-width: 0;
  height: 40px;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #1d2129;
  background: #fafafa;
  box-sizing: border-box;
}
.bf-input--full {
  width: 100%;
}
.bf-range {
  display: flex;
  align-items: center;
  gap: 8px;
}
.bf-sep {
  font-size: 13px;
  color: #969799;
  flex: none;
}
.bf-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #1d2129;
  background: #fafafa;
}
.bf-select .van-icon {
  color: #969799;
  font-size: 14px;
}
.bf-placeholder {
  color: #c8c9cc;
}
.bf-actions {
  display: flex;
  gap: 12px;
  padding: 18px 16px 24px;
  position: sticky;
  bottom: 0;
  background: #fff;
}
.bf-btn {
  flex: 1;
  height: 44px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}
.bf-btn--ghost {
  border: 1px solid #ebedf0;
  color: #323233;
  background: #fff;
}
.bf-btn--primary {
  color: #fff;
  background: #1989fa;
}
.cd-form__actions {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border-top: 1px solid #f2f3f5;
}
.cd-form__actions .van-button {
  flex: 1;
  border-radius: 8px;
}

/* ===== 添加弹层：分区卡片 + 自定义表单行（对齐 room_order_form_preview） ===== */
.cd-form--room .cd-form__body {
  background: #f5f6f8;
  padding: 10px 12px 12px;
}

/* 去掉卡片形式：无背景 / 无圆角 / 无阴影 / 无蓝条，字段直接平铺 */
.ro-group {
  background: transparent;
  border-radius: 0;
  margin-bottom: 10px;
  overflow: visible;
  box-shadow: none;
  border: none;
  border-left: none;
  padding: 0;
}
/* 分组标题：与客户表单 .form-divider 完全一致（13px 600 + 左 3px 蓝条 + 浅灰底） */
.ro-group__hd {
  font-size: 13px;
  font-weight: 600;
  color: #2b5fd9;
  padding: 14px 16px 8px;
  margin-top: 6px;
  border-left: 3px solid #2b5fd9;
  background: #fafbfc;
  display: block;
}
.ro-group__hd::before {
  display: none;
}
/* 只读详情弹层：表单已平铺，但详情是「看」，保留分组卡片（蓝点式分组标题） */
.ro-group--card {
  background: #fff;
  border-radius: 16px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(20, 40, 80, 0.06);
  padding: 0 14px 10px;
}
.ro-group--card .ro-group__hd {
  margin-top: 0;
  border-left: none;
  background: transparent;
  padding: 14px 0 10px;
  color: #1d2129;
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 7px;
}
.ro-group--card .ro-group__hd::before {
  display: block;
  content: "";
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #2b5fd9;
  flex: none;
}
.ro-group--card .ro-field {
  padding: 6px 0;
  border-top: 1px solid #f2f3f5;
}

/* 单行：label + 值/输入 + 箭头 */
.ro-field {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 16px;
  border-top: 1px solid #f5f6f8;
  background: #fff;
}

/* 【P100】表单内提示条（如「余额支付免财务审核」）：纯展示，不进 payload */
.ro-notice {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin: 4px 0 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--brand-soft, #eef2fb);
  border: 1px solid #dbe6fb;
  font-size: 12.5px;
  line-height: 1.6;
  color: #2b4a8b;
}
.ro-notice__ic {
  flex: none;
  font-size: 14px;
  margin-top: 1px;
  color: var(--brand, #2b5fd9);
}
.ro-notice__tx {
  flex: 1;
  min-width: 0;
}
.ro-notice--warn {
  background: #fff5e6;
  border-color: #ffe2b0;
  color: #a96a00;
}
.ro-notice--warn .ro-notice__ic {
  color: #e08f00;
}
.ro-group .ro-field:first-of-type,
.ro-group .ro-block:first-of-type {
  border-top: none;
}
/* 标题正下方的首行不画线（:first-of-type 被 hd 占位，这里显式兜底） */
.ro-group__hd + .ro-field,
.ro-group__hd + .ro-block {
  border-top: none;
}
.ro-label {
  width: 88px;
  flex: none;
  font-size: 14px;
  color: #646566;
}
.ro-req {
  color: #ee0a24;
  font-style: normal;
  margin-right: 2px;
}
.ro-val {
  flex: 1;
  font-size: 14px;
  color: #323233;
  display: flex;
  align-items: center;
  word-break: break-all;
}
.ro-val--ph {
  color: #c8c9cc;
}
/* 只读详情：值右对齐（表单里是左对齐输入，详情是展示） */
.ro-val--right {
  justify-content: flex-end;
  text-align: right;
}
/* 详情无字段时的占位 */
.ro-empty {
  padding: 34px 0;
  text-align: center;
  font-size: 13px;
  color: #8c8f94;
}
.ro-arrow {
  margin-left: auto;
  color: #c8c9cc;
  font-size: 15px;
  padding-left: 6px;
}
.ro-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #323233;
  background: transparent;
  width: 100%;
  padding: 0;
  -webkit-appearance: none;
}
.ro-input::placeholder {
  color: #c8c9cc;
}

/* 块级：label 独占一行（chips / range / textarea） */
.ro-block {
  padding: 10px 16px 12px;
  border-top: 1px solid #f5f6f8;
  background: #fff;
}
.ro-block .ro-label {
  width: auto;
  display: block;
  margin-bottom: 8px;
}

/* 时间区间 */
.ro-range {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ro-range .ro-input {
  height: 40px;
  background: #f7f8fa;
  border-radius: 8px;
  padding: 0 10px;
  text-align: center;
}
.ro-tilde {
  color: #c8c9cc;
  flex: none;
}

/* 【P33】使用时间：点击弹出滑动时间选择器（替代裸文本输入，消除手输格式错误） */
.ro-input--pick {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #323233;
  font-size: 14px;
  height: 40px;
  background: #f7f8fa;
  border-radius: 8px;
}
.ro-pick--ph { color: #c8c9cc; }

/* 多选 chips */
.ro-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ro-chip {
  font-size: 13px;
  padding: 6px 13px;
  border-radius: 16px;
  background: #f2f3f5;
  color: #646566;
  border: 1px solid transparent;
  transition: all 0.15s;
}
.ro-chip--on {
  background: #eef2fb;
  color: #2b5fd9;
  border-color: #b9cdf5;
  font-weight: 600;
}
/* 【P9】全选 chip：虚线边框区分 */
.ro-chip--all {
  border: 1px dashed #b9cdf5;
  background: #fff;
  color: #2b5fd9;
}
.ro-chip--all.ro-chip--on {
  background: #2b5fd9;
  color: #fff;
  border-style: solid;
  border-color: #2b5fd9;
}

/* 回款计划行 */
.ro-plans {
  margin-top: 2px;
}
.ro-plan {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}
.ro-plan__cell {
  flex: 1;
  height: 40px;
  background: #f7f8fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  font-size: 13px;
  color: #323233;
  border: none;
  outline: none;
  width: 100%;
  min-width: 0;
}
.ro-plan__cell--amt {
  -webkit-appearance: none;
}
.ro-plan__cell--amt::placeholder {
  color: #c8c9cc;
}
.ro-plan__del {
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 50%;
  background: #fdeaea;
  color: #ee0a24;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}
.ro-addplan {
  margin-top: 12px;
  height: 40px;
  border: 1px dashed #b9cdf5;
  border-radius: 8px;
  color: #2b5fd9;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f9ff;
}

/* 文本域 */
.ro-textarea {
  margin-top: 2px;
}
.ro-textarea--alone {
  margin-top: 0;
}
.ro-textarea textarea {
  width: 100%;
  height: 74px;
  border: none;
  background: #f7f8fa;
  border-radius: 8px;
  padding: 10px;
  font-size: 14px;
  color: #323233;
  outline: none;
  resize: none;
  font-family: inherit;
}

/* 调试信息（默认隐藏，仅 ?debug=1 时可见） */
.ro-debug {
  padding: 8px 12px;
  font-size: 12px;
  color: #ee0a24;
  background: #fff5f5;
  border-top: 1px solid #ffd6d6;
  word-break: break-all;
}

/* 只读字段的锁标记（如「合同」：由当前合同自动带出，不允许改） */
.ro-lock {
  flex: none;
  margin-left: 6px;
  font-size: 14px;
  color: #c8c9cc;
}

/* 字段说明（如回款计划「合计需等于销售价格」） */
.ro-hint {
  width: 100%;
  margin: 2px 0 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #969799;
}

/* 底部按钮（整页表单的吸底操作条） */
.ro-actions {
  /* 不再是 sticky：作为 flex 固定项真正贴底，不随字段区滚动 */
  position: static;
  flex: none;
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border-top: 1px solid #f2f3f5;
}
/* 与客户表单底部操作条一致：8px 圆角，取消=白底描边 / 保存=主色 #2b5fd9 */
.ro-btn {
  flex: 1;
  height: 44px;
  border-radius: 8px;
  border: none;
  font-size: 15px;
  font-weight: 500;
  margin: 0;
}
.ro-btn--cancel {
  background: #fff;
  border: 1px solid #dcdee0;
  color: #323233;
}
.ro-btn--save {
  background: #2b5fd9;
  color: #fff;
}
.ro-btn--save:disabled {
  opacity: 0.6;
}

/* ===== 删除确认弹窗 =====
   已全站统一到 components/ConfirmDialog.vue（居中弹窗），
   原 .cd-del-pop / .cd-del-* 自定义样式（带垃圾桶图标 + 名称信息行）已下线，勿再启用。 */

/* ===== 通用面板卡片（进度 / 合同内容 / 业务单据） ===== */
.cd-panel {
  background: #fff;
  border-radius: 12px;
  margin: 12px 12px 0;
  padding: 14px 16px 16px;
}
.cd-panel__hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.cd-sec__title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}
.cd-panel__link {
  font-size: 13px;
  color: #2b5fd9;
}
.cd-panel__hint {
  font-size: 12px;
  color: #a8afb8;
}

/* ===== 进度情况（stage 1待执行 2已预定 3已支付 4已全款） ===== */
.cd-progress__pct {
  font-size: 13px;
  font-weight: 600;
  color: #2b5fd9;
}
.cd-progress__bar {
  height: 8px;
  border-radius: 4px;
  background: #f1f2f4;
  overflow: hidden;
}
.cd-progress__fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(135deg, #2b5fd9 0%, #4a8af0 100%);
  transition: width .3s ease;
}
.cd-steps {
  display: flex;
  margin-top: 16px;
}
.cd-step {
  flex: 1;
  text-align: center;
  position: relative;
}
.cd-step::after {
  content: "";
  position: absolute;
  top: 11px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #eef0f3;
}
.cd-step:last-child::after {
  display: none;
}
.cd-step--on::after {
  background: #2b5fd9;
}
.cd-step__dot {
  position: relative;
  z-index: 1;
  width: 22px;
  height: 22px;
  margin: 0 auto 6px;
  border-radius: 50%;
  background: #eef0f3;
  color: #a8afb8;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cd-step--on .cd-step__dot {
  background: #2b5fd9;
  color: #fff;
}
.cd-step--cur .cd-step__dot {
  box-shadow: 0 0 0 4px rgba(43, 95, 217, .16);
}
.cd-step__label {
  font-size: 12px;
  color: #a8afb8;
}
.cd-step--on .cd-step__label {
  color: #2b5fd9;
}

/* ===== 合同字段分组（业务信息 / 会议信息 / 系统信息） ===== */
.cd-fold {
  display: flex;
  align-items: center;
  margin-top: 0;
  padding: 10px 12px;
  background: #f7f8fa;
  border-radius: 8px;
  cursor: pointer;
}
.cd-fold__title {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
}
.cd-fold__hint {
  flex: 1;
  text-align: right;
  padding-right: 6px;
  font-size: 13px;
  color: #2b5fd9;
}
.cd-fold__arrow {
  color: #a8afb8;
}
.cd-fold__body {
  margin-top: 8px;
}

/* 金额字段两列宫格 */
.cd-mgrid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 4px;
}
.cd-mgrid__cell {
  background: #fbfcfd;
  border: 1px solid #f0f1f3;
  border-radius: 8px;
  padding: 8px 10px;
}
.cd-mgrid__label {
  font-size: 12px;
  color: #86909c;
  margin-bottom: 4px;
}
.cd-mgrid__value {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}

/* ===== 业务单据：小图标宫格 ===== */
.cd-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 8px;
}
.cd-grid__item {
  text-align: center;
  padding: 8px 0 4px;
}
.cd-grid__icon {
  position: relative;
  width: 44px;
  height: 44px;
  margin: 0 auto 6px;
  border-radius: 12px;
  background: #eef3ff;
  color: #2b5fd9;
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cd-grid__badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ee0a24;
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.cd-grid__label {
  font-size: 12px;
  color: #4e5969;
}

/* ===== 单据独立页面（client 内切换，不跳路由） ===== */
.cd-billpage {
  /* 底部留白移到列表容器，避免表单模式下把底部按钮顶起来 */
  padding: 0;
}

/* ============================================================
 * 单据列表 / 详情：1:1 沿用 client.vue 主列表（客户·商机·合同）样式
 * .list-box / .crm-item / .customer-card__* / .status-tag /
 * .info-row·.info-col·.info-col1 / .btn-action
 * 不新增平行视觉语言，保证全站列表观感一致
 * ============================================================ */
.list-box {
  width: 100%;
  padding: 0 14px 24px;
  box-sizing: border-box;
  /* 与客户列表标准一致：顶栏与数据块之间留 20px，避免 sticky 顶栏切掉卡片 */
  margin-top: 20px;
}
/* 空态（模板里一直有 bl-empty，但此前无对应样式） */
.bl-empty {
  margin-top: 20px;
  padding: 64px 24px;
  text-align: center;
}
.bl-empty__icon {
  font-size: 46px;
  color: #ccd2dc;
  margin-bottom: 12px;
  line-height: 1;
}
.bl-empty__text {
  font-size: 14px;
  color: #8c8f94;
}
.bl-empty__hint {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 6px;
}
.crm-item {
  background: #fff;
  border-radius: 16px;
  margin-bottom: 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(31, 58, 110, 0.07);
  /* 与客户列表一致：卡片左侧不显示蓝色竖条 */
  border-left: none;
}
.item-head {
  padding: 16px 16px 14px;
  border-bottom: 1px solid #f2f3f5;
}
.customer-card.item-head {
  border-bottom: none;
  padding: 14px 14px 12px;
}
.customer-card__main {
  display: flex;
  gap: 14px;
}
.customer-card__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.customer-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.customer-card__name-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.customer-card__name {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.customer-card__role {
  flex-shrink: 0;
  font-size: 10px;
  color: #2b5fd9;
  background: #eef2fb;
  border: 1px solid #d6e0f5;
  border-radius: 10px;
  padding: 1px 6px;
  font-weight: 500;
}
.customer-card__row {
  font-size: 12px;
  color: #4e5969;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.customer-card__row--muted {
  color: #8c8f94;
}
/* 【P227】会议室名称单独一行：14px 加粗，颜色随卡片主色（不用 muted 灰） */
.customer-card__row--room {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d21;
  margin-bottom: 4px;
}
.customer-card__actions {
  display: flex;
  gap: 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f2f3f5;
}
.btn-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 0;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-action--secondary {
  color: #2b5fd9;
  background: #eef2fb;
}
.btn-action--secondary:active {
  background: #d6e0f5;
}
.btn-action--primary {
  color: #fff;
  background: linear-gradient(135deg, #2b5fd9, #4a8af0);
  box-shadow: 0 3px 10px rgba(43, 95, 217, 0.2);
}
.btn-action--primary:active {
  opacity: 0.9;
}
.btn-action--danger {
  color: #f53f3f;
  background: #ffece8;
}
.btn-action--danger:active {
  background: #ffd9d2;
}

/* 状态标签（与主列表同一套） */
.status-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px 2px 8px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.6;
  flex-shrink: 0;
}
.status-tag::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
.tag-danger { background: #ffece8; color: #f53f3f; }
.tag-wait { background: #fff7e8; color: #d9760a; }
.tag-allot { background: #eef2fb; color: #2b5fd9; }
.tag-receive { background: #e8ffea; color: #00a62a; }

/* ===== 整页表单：新增 / 编辑（替代原底部弹层，沿用 ro-* 表单样式） ===== */
.cd-formpage {
  display: flex;
  flex-direction: column;
  /* 底部弹窗内：撑满 popup 高度，配合 body 的 flex:1 + overflow-y:auto，
     实现「标题与底部按钮固定，只有字段区滚动」——对齐客户表单 form-page */
  height: 100%;
  background: #f5f7fb;
}
.cd-formpage__body {
  flex: 1;
  min-height: 0; /* flex 子项可收缩，否则 overflow-y 不生效 */
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #f5f7fb;
  padding: 0 0 12px;
}

/* ===== 【P95】三.00 销售合同审批区 ===== */
.cd-approval{
  margin: 10px 12px 0;
  background: #fff;
  border: 1px solid #ffe2b0;
  border-radius: 14px;
  padding: 14px 16px;
  box-shadow: 0 2px 10px rgba(224,143,0,.08);
}
.cd-approval__top{ display:flex; align-items:center; gap:8px; margin-bottom:8px; }
.cd-approval__badge{
  background:#fff5e6; color:#e08f00; font-weight:700; font-size:12px;
  border-radius:999px; padding:2px 10px;
}
.cd-approval__who{ font-size:12.5px; color:#8a93a5; }
.cd-approval__desc{ font-size:12.5px; color:#4a5568; line-height:1.7; margin-bottom:12px; }
.cd-approval__desc b{ color:#e08f00; }
.cd-approval__bar{ display:flex; gap:10px; }
.cd-approval__bar .van-button{ flex:1; }
.cd-approval__bar--muted{
  justify-content:center; font-size:12.5px; color:#8a93a5;
  background:#f7f8fa; border-radius:10px; padding:9px 0;
}
/* 【P97】审批区三态配色：待审批=橙 / 已通过=绿 / 已驳回=红 */
.cd-approval--pass{
  border-color:#cdebd9;
  box-shadow:0 2px 10px rgba(23,164,91,.08);
}
.cd-approval--pass .cd-approval__badge{ background:#e8f6ee; color:#17a45b; }
.cd-approval--reject{
  border-color:#f8d3d1;
  box-shadow:0 2px 10px rgba(227,77,89,.08);
}
.cd-approval--reject .cd-approval__badge{ background:#feeceb; color:#e34d59; }
.cd-approval__desc--reject{ color:#e34d59; }
.cd-approval__desc--reject b{ color:#e34d59; }
/* 【P105】审批列表：审核操作 / 批量选择 / 驳回弹层 */
.apx-actions{ justify-content:flex-end; gap:8px; }
/* 【P106】审核结果横幅（不依赖 toast） */.apx-result{
  display:flex; align-items:flex-start; gap:8px;
  margin:10px 0 0; padding:9px 12px; border-radius:10px;
  font-size:12.5px; line-height:1.6; border:1px solid transparent;
}
.apx-result__dot{ flex-shrink:0; width:7px; height:7px; border-radius:50%; margin-top:6px; background:currentColor; }
.apx-result__txt{ flex:1; min-width:0; word-break:break-all; }
.apx-result__x{ flex-shrink:0; opacity:.6; padding:0 2px; }
.apx-result--ok{ color:#17a45b; background:#e8f6ee; border-color:#cdebd9; }
.apx-result--err{ color:#e34d59; background:#feeceb; border-color:#f8d3d1; }
.apx-result--warn{ color:#d9821a; background:#fff6e8; border-color:#f7e0bc; }
.apx-result--info{ color:#2b5fd9; background:#eef2fb; border-color:#d6e0f5; }
/* 【P106】通过弹层里的待审内容预览 */
.apx-approve__list{
  margin-top:8px; padding:8px 10px; border-radius:8px; background:#f7f8fa;
  font-size:12px; color:#4e5969; line-height:1.65; max-height:150px; overflow:auto;
}
.apx-approve__item{ padding:3px 0; word-break:break-all; }
.apx-approve__item + .apx-approve__item{ border-top:1px dashed #e5e8ee; }
.apx-approve__more{ padding-top:4px; color:#8a93a5; }
/* 【P108f】详情操作条（内联版）：商机详情的 .cust-detail__bar 是 popup 内 absolute 定位，
   合同详情是普通滚动页面 → 覆盖为 static 并加圆角卡片感，按钮样式完全复用（.btn/.btn--*） */
.cust-detail__bar--inline{
  position: static;
  margin: 14px 16px 0;
  border-radius: 12px;
  border: 1px solid #eef0f4;
  box-shadow: none;
}
/* 【P108】筛选面板：滚轮日期选择触发块（替换原生 input[type=date]） */
.bf-input--pick{ display:flex; align-items:center; min-height:34px; cursor:pointer; }
.bf-input--pick.bf-placeholder{ color:#c8c9cc; }
/* 【P107】会议室订单：审核状态标签行 / 未通过整行标红 / 从审核跳转定位高亮 / 跳转入口 */
.bill-flags{ display:flex; flex-wrap:wrap; gap:6px; margin-top:2px; }
.crm-item--rejected{
  border-left:3px solid #e34d59;
  background:linear-gradient(90deg,#fef6f6 0%,#fff 42%);
}
.crm-item--flash{
  box-shadow:0 0 0 2px #2b5fd9, 0 6px 22px rgba(43,95,217,.22) !important;
  transition:box-shadow .25s ease;
}
.apx-card__link{
  display:flex; align-items:center; gap:5px; margin-top:10px;
  padding:8px 10px; border-radius:9px;
  background:#eef2fb; border:1px solid #d6e0f5;
  font-size:12.5px; font-weight:500; color:#2b5fd9;
}
.apx-card__link-txt{ flex:1; }
.apx-card__link-arrow{ opacity:.7; }
.apx-card__link-ico{ font-size:15px; }
/* 【P105g2】审批列表专用卡片：全量内容换行展示（不截断）+ 元信息 + 审批意见 */
.apx-card{ display:block; width:100%; min-width:0; }
.apx-card__head{
  display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:8px;
}
.apx-card__type{
  font-size:11px; font-weight:600; color:#2b5fd9;
  background:#eef2fb; border:1px solid #d6e0f5; border-radius:10px; padding:1px 7px;
}
.apx-card__scope{
  font-size:11px; font-weight:600; border-radius:10px; padding:1px 7px; border:1px solid transparent;
}
.apx-card__scope--contract{ color:#7a5af8; background:#f2effe; border-color:#e0d9fd; }
.apx-card__scope--room{ color:#0f9b8e; background:#e8f8f6; border-color:#c8ece8; }
.apx-card__desc{
  font-size:13px; color:#1d2129; line-height:1.62;
  white-space:normal; word-break:break-all; overflow:visible;
}
.apx-card__meta{
  display:flex; flex-wrap:wrap; gap:4px 12px; margin-top:8px;
  font-size:11.5px; color:#8a93a5;
}
.apx-card__remark{
  margin-top:8px; padding:7px 9px; border-radius:8px;
  background:#f7f8fa; font-size:12px; color:#4e5969; line-height:1.6;
  white-space:normal; word-break:break-all;
}
.apx-card__remark em{
  font-style:normal; font-weight:600; color:#e34d59; margin-right:6px;
}
.apx-hint{ justify-content:flex-start; }
.apx-hint__txt{ font-size:12px; color:#8a93a5; }
.apx-item--batch{ padding-left:34px; position:relative; }
.apx-item__check{ position:absolute; top:16px; left:8px; z-index:3; }
.apx-batch-bar{
  display:flex; align-items:center; justify-content:space-between;
  padding:8px 14px; background:#fff; border-bottom:1px solid #eee; font-size:13px; color:#4e5969;
}
.apx-batch-bar__count{ color:#2b5fd9; font-weight:600; }
.apx-batch-actions{
  position:fixed; left:0; right:0; bottom:50px; z-index:50;
  display:flex; align-items:center; justify-content:space-between;
  padding:8px 14px; background:#fff; box-shadow:0 -2px 10px rgba(0,0,0,.08); border-top:1px solid #eee;
}
.apx-batch-actions__count{ font-size:13px; color:#4e5969; }
.apx-batch-actions__btns{ display:flex; gap:10px; }
.apx-reject__body{ padding:16px 14px 18px; }
.apx-reject__title{ font-size:16px; font-weight:600; color:#1d2129; text-align:center; margin-bottom:8px; }
.apx-reject__tip{ font-size:12.5px; color:#4e5969; line-height:1.7; margin-bottom:10px; }
.apx-reject__tip em{ font-style:normal; color:#e34d59; }
.apx-reject__btns{ display:flex; gap:10px; margin-top:14px; }
.cd-approval--pass .cd-approval__desc{ margin-bottom:0; }
.cd-reject__hd{ text-align:center; font-weight:700; font-size:15px; padding:16px 0 6px; color:#1c2437; }
.cd-reject__bd{ padding: 0 12px 6px; }
.cd-reject__bar{ display:flex; gap:10px; padding: 8px 14px 18px; }
.cd-reject__bar .van-button{ flex:1; }

/* 【P95】三.1.01 导航栏删除入口 */
.cd-nav__del{ color:#e34d59; font-size:14px; font-weight:600; padding:4px 6px; }

/* 【P214】多选弹层入口行 + 摘要 */
.ro-multi-picker{display:flex;align-items:center;justify-content:space-between;
  padding:10px 12px;border:1px solid #ebedf0;border-radius:8px;background:#fff;cursor:pointer;}
.ro-multi-picker__txt{flex:1;font-size:13px;color:#323233;line-height:1.5;word-break:break-all;}
.ro-multi-picker__sub{display:block;margin-top:2px;font-size:12px;color:#969799;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.ro-multi-picker__arrow{color:#c8c9cc;font-size:16px;padding-left:8px;flex-shrink:0;}

/* 【P215】「保存并继续」次要按钮：描边样式，层级低于「保存」 */
.ro-btn--cont{background:#fff;color:#1989fa;border:1px solid #1989fa;}
.ro-btn--cont:disabled{opacity:.55;}
</style>



// WEBPACK FOOTER //
// src/pages/contractDetail.vue